/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useDrillStack } from './useDrillStack';
import { api } from './apiClient';
import { theme, fmtNum } from './theme';
import { WarehouseRow } from './useWarehouseRows';
import { useAliases } from './useAliases';

/* Resolve a chamber label that honors the per-warehouse rename stored as
 * JSON in aliases.area["<WAREHOUSE>-<CHAMBER>"]. Falls back to the legacy
 * global area alias, then to the raw code. Output format: "<code> · <desc>"
 * when a description is set, otherwise the raw code. */
function getChamberLabel(
  aliasR: (cat: 'area', code: string) => string,
  wh: string | undefined | null,
  area: string,
): string {
  if (!area) return '';
  // 1. Per-warehouse chamber alias (composite key)
  if (wh) {
    const compositeKey = `${wh}-${area}`;
    const compositeRaw = aliasR('area', compositeKey);
    if (compositeRaw && compositeRaw !== compositeKey) {
      try {
        const parsed = JSON.parse(compositeRaw);
        if (parsed && typeof parsed === 'object' && parsed.desc) {
          return `${area} · ${parsed.desc}`;
        }
      } catch { /* not JSON */ }
      return compositeRaw;
    }
  }
  // 2. Legacy global area alias (bare code)
  const bareRaw = aliasR('area', area);
  if (bareRaw && bareRaw !== area) {
    try {
      const parsed = JSON.parse(bareRaw);
      if (parsed && typeof parsed === 'object' && parsed.desc) {
        return `${area} · ${parsed.desc}`;
      }
    } catch { /* not JSON */ }
    return bareRaw;
  }
  // 3. Raw code
  return area;
}

/* DrillModal — full-screen Power BI-style drill experience.
 *
 *  Receives the *already-filtered* warehouse rows from the dashboard so the
 *  drill numbers always match the cards. Drill levels:
 *    Level 0 → break down by WAREHOUSE
 *    Level 1 → break down by STORAGE_AREA (CH) inside the picked warehouse
 *    Level 2 → break down by SHELF_SIZE  (Pallet Type) inside the picked area
 *    Level 3 → break down by BIN_LOCATION (LPREG-LPHOR-LPVER) inside the
 *              picked pallet type — the deepest physical level.
 *
 *  KPIs are aggregated using the metric mapped from `kpiKey`:
 *    - 'COUNT' KPIs (totalLocations, blockedLocations) count rows per group
 *    - 'SUM:<field>' KPIs sum a numeric field per group
 */

type Row = { key: string; label: string; value: number };

export type WarehouseBreakdown = {
  binCount: number;
  binUsed: number;
  binCap: number;
  manualCap: number;
  bin: number; virtual: number; floor: number; manual: number;
  blocked: number; tempBlocked: number; areaBlocked: number; commit: number;
  totalCap: number; totalUsed: number; free: number;
  rackByArea?: Record<string, { cap: number; used: number; binCount: number }>;
  manualByLocation?: Record<string, { cap: number; used: number }>;
  areaBlockedByArea?: any[];
};

type Props = {
  kpiKey: string;
  kpiTitle: string;
  accent: string;
  rows: WarehouseRow[];
  perWarehouse?: Record<string, WarehouseBreakdown>;
  customerStorage?: any[];
  commitments?: CommitmentRow[];
  onClose: () => void;
};

export type CommitmentRow = {
  id?: number;
  customer: string;
  warehouse: string;
  warehouses: string[];
  type: string;
  unit: string;
  value: number;
  factor: number;
  minPallets: number;
  actual: number;
  shortfall: number;
  isMet: boolean;
  storageType: string;
  notes: string;
};

const num = (v: any) => (Number.isFinite(Number(v)) ? Number(v) : 0);

const PIE_COLORS = ['#2563eb', '#0891b2', '#16a34a', '#7c3aed', '#d97706', '#dc2626', '#db2777', '#4338ca', '#a16207', '#0e7490'];

type Metric = { kind: 'count' } | { kind: 'sum'; field: keyof WarehouseRow };

/** Each KPI maps to either a row count or a numeric field sum */
const metricFor = (kpiKey: string): Metric => {
  switch (kpiKey) {
    case 'totalLocations':   return { kind: 'count' };
    case 'blockedLocations': return { kind: 'count' };
    case 'usedCapacity':     return { kind: 'sum', field: 'USED_BINS' };
    case 'freeCapacity':     return { kind: 'sum', field: 'FREE_BINS' };
    case 'blockedCapacity':  return { kind: 'sum', field: 'TEMP_BLOCKED_BINS' };
    default:                 return { kind: 'sum', field: 'TOTAL_BINS' };
  }
};

// Capacity: seg 1-2 → as-is, seg 3-9 + level=10 → 2, seg 3-9 + level≠10 → 1, seg ≥10 → as-is.
const drillCalcCap = (r: WarehouseRow): number => {
  const s = num(r.SEG), l = num(r.LVL);
  if (s <= 0) return 0;
  if (s <= 2) return s;
  if (s < 10) return l === 10 ? 2 : 1;
  return s;
};

const aggregateRows = (rows: WarehouseRow[], metric: Metric, groupBy: keyof WarehouseRow): Row[] => {
  const byKey: Record<string, number> = {};
  rows.forEach(r => {
    const k = String(r[groupBy] ?? '');
    if (!k) return;
    if (metric.kind === 'count') {
      byKey[k] = (byKey[k] || 0) + 1;
    } else {
      // For capacity metric use calcCap; for used/other metrics use raw value
      const val = metric.field === 'TOTAL_BINS' ? drillCalcCap(r) : num(r[metric.field]);
      byKey[k] = (byKey[k] || 0) + val;
    }
  });
  return Object.entries(byKey)
    .map(([k, v]) => ({ key: k, label: k, value: v }))
    .sort((a, b) => b.value - a.value);
};

const DrillModal: React.FC<Props> = ({ kpiKey, kpiTitle, accent, rows, perWarehouse, customerStorage, commitments, onClose }) => {
  const drill = useDrillStack(kpiTitle);
  const { resolve: aliasResolve } = useAliases();
  const [view, setView] = useState<'bar' | 'donut'>('bar');
  const [search, setSearch] = useState('');
  // Lazy-loaded bin-level rows (only fetched when drill reaches level 3)
  const [binRows, setBinRows] = useState<Row[]>([]);
  const [binLoading, setBinLoading] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { drill.reset(); setSearch(''); /* eslint-disable-next-line */ }, [kpiKey]);
  useEffect(() => { setSearch(''); }, [drill.current.level]);

  /** Download the visible drill table as CSV */
  const exportCSV = () => {
    const header = ['#', levelLabel, 'Value', '%'];
    const totalSum = baseRows.reduce((s, r) => s + r.value, 0) || 1;
    const lines = [header.join(',')];
    tableRows.forEach((r, i) => {
      const pct = ((r.value / totalSum) * 100).toFixed(2);
      lines.push([i + 1, `"${r.label.replace(/"/g, '""')}"`, r.value, pct + '%'].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kpiTitle.replace(/\s+/g, '_')}_${levelLabel.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Open the browser print dialog — user can "Save as PDF" from there.
   *  We add a body class so a print stylesheet can hide everything except
   *  the drill modal. */
  const exportPDF = () => {
    document.body.classList.add('drill-print-mode');
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove('drill-print-mode'), 200);
    }, 50);
  };

  const metric = metricFor(kpiKey);

  /* Build the visible drill table from the filtered rows.
   * Drill chain: Warehouse → Storage Area → Pallet Type → Bin Location.
   * Each level applies all parent crumbs as filters before grouping. */
  const computedRows: Row[] = useMemo(() => {
    const lvl = drill.current.level;
    let pool = rows;
    if (kpiKey === 'blockedLocations') pool = pool.filter(r => r.BLOCKED);

    const wh = drill.stack[1]?.key;  // chosen warehouse
    const ch = drill.stack[2]?.key;  // chosen storage area / CH
    const pt = drill.stack[3]?.key;  // chosen pallet type
    if (wh) pool = pool.filter(r => r.WAREHOUSE === wh);
    if (ch) pool = pool.filter(r => r.STORAGE_AREA === ch);
    if (pt) pool = pool.filter(r => r.SHELF_SIZE === pt);

    if (lvl === 0) {
      // If breakdown data is available, build rows from it so we can show
      // bin/virtual/floor/manual/blocked/commit per warehouse.
      if (perWarehouse) {
        return Object.entries(perWarehouse).map(([wh, b]) => ({
          key: wh, label: aliasResolve('warehouse', wh),
          value:
            kpiKey === 'totalCapacity'    ? b.totalCap :
            kpiKey === 'freeCapacity'     ? b.free :
            kpiKey === 'blockedCapacity'  ? b.blocked :
            kpiKey === 'committedCapacity'? b.commit :
            kpiKey === 'totalLocations'   ? 1 :
            kpiKey === 'blockedLocations' ? (b.blocked > 0 ? 1 : 0) :
            b.totalUsed,
        })).filter(r => r.value > 0).sort((a, b) => b.value - a.value);
      }
      return aggregateRows(pool, metric, 'WAREHOUSE')
        .map(r => ({ ...r, label: aliasResolve('warehouse', r.key) }));
    }
    if (lvl === 1) return aggregateRows(pool, metric, 'STORAGE_AREA')
      .map(r => ({ ...r, label: aliasResolve('area', r.key) }));
    if (lvl === 2) return aggregateRows(pool, metric, 'SHELF_SIZE')
      .map(r => ({ ...r, label: aliasResolve('palletType', r.key) }));
    if (lvl === 3) return binRows; // lazy-fetched from /warehouse/bin-inventory
    return [];
  }, [drill.current, drill.stack, rows, metric, kpiKey, binRows, perWarehouse, aliasResolve]);

  // Per-warehouse breakdown columns are reserved for the location drill only.
  const showWarehouseBreakdown = false;

  /* When the user enters level 3 (bin location) we hit the rich
   * /warehouse/bin-inventory endpoint which returns LPREG-LPHOR-LPVER
   * joined to the shelf/customer tables. We filter by the active breadcrumb
   * context (warehouse + area + shelf/pallet type). */
  useEffect(() => {
    if (drill.current.level !== 3) return;
    const wh = drill.stack[1]?.key;
    const ch = drill.stack[2]?.key;      // "LKORT-LKBER" combined
    if (!wh || !ch) { setBinRows([]); return; }
    const [lkort, lkber] = ch.split('-');
    setBinLoading(true);
    api.get('/warehouse/bin-inventory', {
      params: { warehouse: wh, storageLocation: lkort, storageArea: lkber },
    })
      .then(r => {
        const list: any[] = r.data?.data ?? (Array.isArray(r.data) ? r.data : []);
        const pt = drill.stack[3]?.key;
        const filtered = pt
          ? list.filter((x: any) => String(x.SHELF_SIZE || '').trim() === pt)
          : list;
        // One row per physical bin. For count-based metrics that's 1 per row;
        // for capacity metrics sum TOTAL_SEGMENTS per bin (single value anyway).
        const mapped: Row[] = filtered.map((x: any) => {
          const key = String(x.BIN_LOCATION || `${x.REG}-${x.HOR}-${x.VER}`).trim();
          let value = 1;
          if (metric.kind === 'sum') {
            const total = Number(x.TOTAL_SEGMENTS || 0);
            const free  = Number(x.FREE_SEGMENTS || 0);
            const used  = Math.max(0, total - free);
            const blocked = x.OCCUPANCY_STATUS === 'TEMP_BLOCKED' || String(x.STATUS_CODE || '').trim() === '2';
            if (metric.field === 'USED_BINS')         value = blocked ? total : used;
            else if (metric.field === 'FREE_BINS')    value = blocked ? 0 : free;
            else if (metric.field === 'TEMP_BLOCKED_BINS') value = blocked ? total : 0;
            else                                      value = total; // TOTAL_BINS == capacity
          }
          return { key, label: key, value };
        }).sort((a, b) => b.value - a.value);
        setBinRows(mapped);
        setBinLoading(false);
      })
      .catch(() => { setBinRows([]); setBinLoading(false); });
  }, [drill.current.level, drill.stack, metric]);

  const baseRows = computedRows;
  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return baseRows;
    return baseRows.filter(r => r.label.toLowerCase().includes(q));
  }, [baseRows, search]);

  const total = useMemo(() => baseRows.reduce((s, r) => s + r.value, 0), [baseRows]);
  const max = useMemo(() => baseRows.reduce((m, r) => Math.max(m, r.value), 0), [baseRows]);
  const avg = baseRows.length ? total / baseRows.length : 0;
  const topRow = baseRows[0];
  const topShare = topRow && total > 0 ? (topRow.value / total) * 100 : 0;
  // Concentration: share of total held by top 3 rows (HHI-lite)
  const top3Share = total > 0
    ? (baseRows.slice(0, 3).reduce((s, r) => s + r.value, 0) / total) * 100
    : 0;

  /* ─────────────────────────────────────────────────────────────
   * SIMPLE LOCATION DRILLOUT (kpiKey === 'totalLocations')
   * Per the user spec: just BIN count + Used segments + Total segments.
   * Level 0 → Warehouses, Level 1 → Areas in selected warehouse,
   * Level 2 → Pallet types in selected area.
   * No chart, no leaderboard, no summary tiles. Just a clean table.
   * ─────────────────────────────────────────────────────────────*/
  if (kpiKey === 'totalLocations') {
    return (
      <LocationDrill
        accent={accent}
        kpiTitle={kpiTitle}
        rows={rows}
        perWarehouse={perWarehouse}
        customerStorage={customerStorage}
        drill={drill}
        onClose={onClose}
      />
    );
  }

  if (kpiKey === 'committedCapacity' || kpiKey === 'shortfallCapacity') {
    return (
      <CommitmentDrill
        accent={accent}
        kpiTitle={kpiTitle}
        commitments={commitments || []}
        drill={drill}
        onClose={onClose}
      />
    );
  }

  if (kpiKey === 'usedCapacity') {
    return (
      <OccupiedDrill
        accent={accent}
        kpiTitle={kpiTitle}
        rows={rows}
        perWarehouse={perWarehouse || {}}
        drill={drill}
        onClose={onClose}
      />
    );
  }

  if (kpiKey === 'totalCapacity' || kpiKey === 'sellableCapacity') {
    return (
      <CapacityDrill
        accent={accent}
        kpiTitle={kpiTitle}
        rows={rows}
        perWarehouse={perWarehouse || {}}
        drill={drill}
        onClose={onClose}
      />
    );
  }

  const loading = binLoading && drill.current.level === 3;
  const canDeeper = drill.current.level < 3;
  const levelLabel =
    drill.current.level === 0 ? 'Warehouse' :
    drill.current.level === 1 ? 'Chamber / Area' :
    drill.current.level === 2 ? 'Pallet Type' :
                                'Bin Location';
  const parentContext = drill.stack.slice(1).map(s => s.label).join(' › ');

  return (
    <div className="drill-print-root" style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(20, 30, 60, 0.45)',
      backdropFilter: 'blur(4px)',
      WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{`
        @media print {
          body.drill-print-mode > *:not(.drill-print-root) { display: none !important; }
          .drill-print-root { position: static !important; background: #fff !important; padding: 0 !important; backdrop-filter: none !important; }
          .drill-print-root > div { box-shadow: none !important; border: none !important; height: auto !important; max-width: none !important; }
          .drill-no-print { display: none !important; }
        }
      `}</style>
      <div style={{
        width: '100%', maxWidth: 1360, height: '92vh',
        background: '#fff',
        border: '1px solid #e5eaf2',
        borderRadius: 16,
        boxShadow: '0 40px 100px rgba(15,25,55,0.30)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: theme.fontFamily,
      }}>
        {/* HEADER */}
        <div style={{
          padding: '22px 28px 18px',
          background: '#fff',
          borderBottom: '1px solid #eef2f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div style={{
              width: 4, height: 44, borderRadius: 2,
              background: accent, flexShrink: 0,
            }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: 0.3 }}>
                Drill-down · Level {drill.current.level + 1} of 4 · {levelLabel}
              </div>
              <h2 style={{ margin: '3px 0 0', color: theme.textPrimary, fontSize: 20, fontWeight: 700, letterSpacing: -0.3 }}>
                {kpiTitle}
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className="drill-no-print">
            <div style={{
              display: 'inline-flex', padding: 3, borderRadius: 9,
              background: '#f1f5fb', border: '1px solid #e5eaf2',
            }}>
              <button onClick={() => setView('bar')} style={segStyle(view === 'bar', accent)}>Bar</button>
              <button onClick={() => setView('donut')} style={segStyle(view === 'donut', accent)}>Donut</button>
            </div>
            <button onClick={exportCSV} title="Export as CSV" style={actionBtn}>
              <span style={{ fontSize: 13 }}>↓</span> CSV
            </button>
            <button onClick={exportPDF} title="Export as PDF (print)" style={actionBtn}>
              <span style={{ fontSize: 13 }}>⎙</span> PDF
            </button>
            <button onClick={onClose} title="Close (Esc)" style={{
              marginLeft: 4, width: 34, height: 34, borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff', cursor: 'pointer',
              fontSize: 16, color: theme.textSecondary, fontWeight: 600,
              fontFamily: 'inherit',
            }}>×</button>
          </div>
        </div>

        {/* SUMMARY STRIP — 6 KPI tiles */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
          gap: 0, padding: '0 28px',
          background: '#fafbfd',
          borderBottom: '1px solid #eef2f8',
        }}>
          <SummaryStat label="Total"        value={fmtNum(total)}                accent={accent} />
          <SummaryStat label="Records"      value={fmtNum(baseRows.length)}      accent={accent} />
          <SummaryStat label="Average"      value={fmtNum(Math.round(avg))}      accent={accent} />
          <SummaryStat label="Max"          value={fmtNum(max)}                  accent={accent} />
          <SummaryStat label="Top item"     value={topRow ? topRow.label : '—'}  accent={accent} sub={topRow ? `${topShare.toFixed(1)}%` : ''} />
          <SummaryStat label="Top 3 share"  value={`${top3Share.toFixed(1)}%`}   accent={accent} last />
        </div>

        {/* BREADCRUMB + SEARCH */}
        <div style={{
          padding: '14px 28px',
          display: 'flex', alignItems: 'center', gap: 14,
          borderBottom: '1px solid #eef2f8',
          background: '#fff',
          flexWrap: 'wrap',
        }}>
          <button onClick={drill.drillBack} disabled={drill.stack.length <= 1}
            style={{
              width: 32, height: 32, borderRadius: 8,
              border: '1px solid #e5eaf2',
              background: drill.stack.length > 1 ? '#fff' : '#fafbfd',
              color: drill.stack.length > 1 ? theme.textPrimary : theme.textMuted,
              cursor: drill.stack.length > 1 ? 'pointer' : 'not-allowed',
              fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            }}>
            ←
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {drill.stack.map((s, i) => {
              const last = i === drill.stack.length - 1;
              return (
                <React.Fragment key={i}>
                  {i > 0 && <span style={{ color: '#cbd5e1', fontSize: 12 }}>/</span>}
                  <span onClick={() => drill.drillTo(i)}
                    style={{
                      cursor: last ? 'default' : 'pointer', fontSize: 13,
                      fontWeight: last ? 700 : 500,
                      color: last ? theme.textPrimary : theme.textSecondary,
                      padding: last ? '4px 10px' : '4px 0',
                      borderRadius: 6,
                      background: last ? '#f1f5fb' : 'transparent',
                    }}>
                    {s.label}
                  </span>
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px', borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              width: 240,
            }}>
              <span style={{ color: theme.textMuted, fontSize: 13 }}>⌕</span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${levelLabel.toLowerCase()}…`}
                style={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 13, fontWeight: 500, color: theme.textPrimary,
                  fontFamily: 'inherit',
                }}
              />
            </div>
            <span style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {loading ? 'Loading…' : `${tableRows.length.toLocaleString()} of ${baseRows.length.toLocaleString()}`}
            </span>
          </div>
        </div>

        {/* BODY — top: chart + leaderboard, bottom: full-width table */}
        <div style={{
          flex: 1, display: 'grid',
          gridTemplateRows: 'minmax(220px, 38%) 1fr',
          gap: 18, padding: 22, overflow: 'hidden',
          background: '#fafbfd',
        }}>
          {/* TOP ROW: chart */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr',
            gap: 18, minHeight: 0,
          }}>
          {/* CHART */}
          <div style={{
            background: '#fff',
            border: '1px solid #eef2f8',
            borderRadius: 12, padding: 18, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 1px 3px rgba(20,40,80,0.04)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 10 }}>
              {view === 'bar' ? `Top ${Math.min(12, tableRows.length)} values` : 'Distribution'}
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              {tableRows.length === 0 ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted }}>
                  {loading ? 'Loading…' : 'No data'}
                </div>
              ) : view === 'bar' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tableRows.slice(0, 12)} margin={{ left: 0, right: 10, top: 10, bottom: 40 }}>
                    <CartesianGrid stroke="#e3e8f0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: theme.textSecondary, fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                    <YAxis tick={{ fill: theme.textSecondary, fontSize: 10 }} tickFormatter={(v) => fmtNum(v)} width={70} />
                    <Tooltip
                      contentStyle={{ background: '#fff', border: `1px solid ${accent}`, borderRadius: 10, color: theme.textPrimary }}
                      formatter={(v: any) => fmtNum(v)}
                      cursor={{ fill: '#eef4fc' }}
                    />
                    <Bar dataKey="value" fill={accent} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={tableRows.slice(0, 8)} dataKey="value" nameKey="label"
                      cx="50%" cy="50%" innerRadius={55} outerRadius={110} paddingAngle={2}>
                      {tableRows.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#fff', border: `1px solid ${accent}`, borderRadius: 10, color: theme.textPrimary }}
                      formatter={(v: any) => fmtNum(v)}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: theme.textSecondary }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          </div>

          {/* TABLE — full width below */}
          <div style={{
            background: '#fff',
            border: '1px solid #eef2f8',
            borderRadius: 12, overflow: 'auto',
            boxShadow: '0 1px 3px rgba(20,40,80,0.04)',
            minHeight: 0,
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: theme.panelBg, zIndex: 1 }}>
                <tr>
                  <th style={{ ...th, width: 38 }}>#</th>
                  <th style={th}>{levelLabel}</th>
                  {showWarehouseBreakdown && <>
                    <th style={{ ...th, textAlign: 'right' }}>Bin</th>
                    <th style={{ ...th, textAlign: 'right' }}>Virtual</th>
                    <th style={{ ...th, textAlign: 'right' }}>Floor</th>
                    <th style={{ ...th, textAlign: 'right' }}>Manual</th>
                    <th style={{ ...th, textAlign: 'right' }}>Blocked</th>
                    <th style={{ ...th, textAlign: 'right' }}>Commit</th>
                    <th style={{ ...th, textAlign: 'right' }}>Total Cap</th>
                  </>}
                  <th style={{ ...th, textAlign: 'right' }}>{showWarehouseBreakdown ? 'Used' : 'Value'}</th>
                  <th style={{ ...th, textAlign: 'right', width: 80 }}>%</th>
                  {canDeeper && <th style={{ ...th, width: 60 }}></th>}
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 && !loading && (
                  <tr><td colSpan={showWarehouseBreakdown ? 12 : 5} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No data</td></tr>
                )}
                {tableRows.map((r, idx) => {
                  const pct = total > 0 ? (r.value / total) * 100 : 0;
                  const barPct = max > 0 ? (r.value / max) * 100 : 0;
                  const isTop = idx < 3 && search === '';
                  return (
                    <tr key={r.key}
                      style={{ borderBottom: `1px solid ${theme.rowBorder}`, transition: 'background .12s' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7faff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <td style={{ ...td, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {idx + 1}
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: theme.textPrimary, fontSize: 13 }}>{r.label}</div>
                        <div style={{
                          marginTop: 6, height: 3, background: '#f1f5fb', borderRadius: 999, overflow: 'hidden',
                        }}>
                          <div style={{
                            width: `${barPct}%`, height: '100%',
                            background: accent, borderRadius: 999,
                          }} />
                        </div>
                      </td>
                      {showWarehouseBreakdown && perWarehouse && (() => {
                        const b = perWarehouse[r.key];
                        const cell = (v: number) => (
                          <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textSecondary }}>
                            {b ? fmtNum(v) : '—'}
                          </td>
                        );
                        return <>
                          {cell(b?.bin ?? 0)}
                          {cell(b?.virtual ?? 0)}
                          {cell(b?.floor ?? 0)}
                          {cell(b?.manual ?? 0)}
                          {cell(b?.blocked ?? 0)}
                          {cell(b?.commit ?? 0)}
                          {cell(b?.totalCap ?? 0)}
                        </>;
                      })()}
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                        {fmtNum(r.value)}
                      </td>
                      <td style={{ ...td, textAlign: 'right', color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {pct.toFixed(1)}%
                      </td>
                      {canDeeper && (
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button
                            onClick={() => drill.drillInto({
                              level: drill.current.level + 1,
                              label: r.label, key: r.key,
                            })}
                            title="Drill in"
                            style={{
                              width: 26, height: 26, borderRadius: 6,
                              border: '1px solid #e5eaf2', background: '#fff',
                              color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                              fontFamily: 'inherit',
                            }}>
                            ›
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot style={{ position: 'sticky', bottom: 0, background: theme.panelBg }}>
                  <tr>
                    <td style={{ ...td, fontWeight: 800, color: theme.textSecondary }} colSpan={showWarehouseBreakdown ? 9 : 2}>
                      TOTAL ({fmtNum(tableRows.length)} rows)
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtNum(tableRows.reduce((s, r) => s + r.value, 0))}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary }}>
                      100%
                    </td>
                    {canDeeper && <td style={td}></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryStat: React.FC<{ label: string; value: string; accent: string; sub?: string; last?: boolean }> = ({ label, value, accent, sub, last }) => (
  <div style={{
    padding: '14px 16px',
    borderRight: last ? 'none' : '1px solid #eef2f8',
    minWidth: 0,
  }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, letterSpacing: 0.4, textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{
      fontSize: 18, fontWeight: 700, color: theme.textPrimary, marginTop: 4,
      fontVariantNumeric: 'tabular-nums', letterSpacing: -0.3,
      display: 'flex', alignItems: 'center', gap: 8,
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
    </div>
    {sub && (
      <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2, fontWeight: 600 }}>{sub}</div>
    )}
  </div>
);

const actionBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '7px 12px', borderRadius: 8,
  border: '1px solid #e5eaf2', background: '#fff',
  color: theme.textSecondary, cursor: 'pointer',
  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
};

const segStyle = (active: boolean, accent: string): React.CSSProperties => ({
  padding: '6px 14px', borderRadius: 7, border: 'none',
  background: active ? '#fff' : 'transparent',
  color: active ? accent : theme.textSecondary,
  fontSize: 12, fontWeight: 600,
  cursor: 'pointer',
  boxShadow: active ? '0 1px 3px rgba(20,40,80,0.10)' : 'none',
  fontFamily: 'inherit',
});

const chipStyle = (active: boolean, accent: string): React.CSSProperties => ({
  padding: '8px 12px',
  borderRadius: 9,
  border: `1px solid ${active ? accent : '#d3dbe8'}`,
  background: active ? `${accent}15` : '#fff',
  color: active ? accent : theme.textPrimary,
  fontSize: 12, fontWeight: 700,
  cursor: 'pointer',
});

const th: React.CSSProperties = {
  padding: '12px 16px', textAlign: 'left',
  fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
  color: theme.textMuted,
  borderBottom: '1px solid #eef2f8',
  background: '#fafbfd',
};

const td: React.CSSProperties = {
  padding: '12px 16px', color: theme.textPrimary, fontWeight: 500,
};

/* ─────────────── Shared View Toggle + Dashboard helper ───────────────
 * Every drillout supports two modes:
 *   • Table     — the dense data grid (default, exhaustive)
 *   • Dashboard — visual summary: ranking bar + donut + top-N cards
 * The helpers below are reused by LocationDrill / CapacityDrill /
 * OccupiedDrill / CommitmentDrill so all four feel consistent.
 * ──────────────────────────────────────────────────────────────────── */

type ViewMode = 'table' | 'dashboard';

const DrillViewToggle: React.FC<{ view: ViewMode; setView: (v: ViewMode) => void; accent: string }> = ({ view, setView, accent }) => {
  const btn = (active: boolean): React.CSSProperties => ({
    padding: '7px 14px',
    borderRadius: 7,
    border: 'none',
    background: active ? '#fff' : 'transparent',
    color: active ? accent : theme.textMuted,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: active ? '0 1px 3px rgba(15,25,55,0.15)' : 'none',
    transition: 'all 0.15s',
  });
  return (
    <div style={{
      display: 'inline-flex', padding: 4, borderRadius: 9,
      background: '#eef2f8', border: '1px solid #e5eaf2', gap: 2,
    }}>
      <button onClick={() => setView('dashboard')} style={btn(view === 'dashboard')}>📊 Dashboard</button>
      <button onClick={() => setView('table')}     style={btn(view === 'table')}>▦ Table</button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────────────
 * SQM ↔ Pallet Calculator (shared across all drillouts)
 *  - Side drawer
 *  - Default types: 95 (1.2 SQM/pallet), 90 (0.90 SQM/pallet)
 *  - User-defined custom types persist in localStorage
 *  - Mixed breakdown: split a total SQM across multiple types by % share,
 *    showing the resulting pallet count for each type.
 * ──────────────────────────────────────────────────────────────────────────── */

type PalletType = { id: string; name: string; sqmPerPallet: number; builtin?: boolean };

const DEFAULT_PALLET_TYPES: PalletType[] = [
  { id: 't95', name: 'Type 95', sqmPerPallet: 1.2,  builtin: true },
  { id: 't90', name: 'Type 90', sqmPerPallet: 0.90, builtin: true },
];

const PALLET_TYPES_LS_KEY = 'v2.palletTypes.v1';

const loadPalletTypes = (): PalletType[] => {
  try {
    const raw = localStorage.getItem(PALLET_TYPES_LS_KEY);
    if (!raw) return DEFAULT_PALLET_TYPES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed;
  } catch {}
  return DEFAULT_PALLET_TYPES;
};

const SqmCalculatorDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  accent: string;
  contextLabel?: string;
}> = ({ open, onClose, accent, contextLabel }) => {
  const [types, setTypes] = useState<PalletType[]>(loadPalletTypes);
  const [direction, setDirection] = useState<'sqm2pal' | 'pal2sqm'>('sqm2pal');
  const [totalSqm, setTotalSqm] = useState<string>('');
  const [totalPal, setTotalPal] = useState<string>('');
  const [shares, setShares] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState('');
  const [newSqm, setNewSqm] = useState('');

  useEffect(() => {
    try { localStorage.setItem(PALLET_TYPES_LS_KEY, JSON.stringify(types)); } catch {}
  }, [types]);

  const totalSqmNum = num(totalSqm);
  const totalPalNum = num(totalPal);
  const sharesNum = useMemo(() => {
    const out: Record<string, number> = {};
    types.forEach(t => { out[t.id] = num(shares[t.id]); });
    return out;
  }, [shares, types]);
  const sharesSum = useMemo(() => Object.values(sharesNum).reduce((s, v) => s + v, 0), [sharesNum]);

  const breakdown = useMemo(() => types.map(t => {
    const pct = sharesSum > 0 ? sharesNum[t.id] / sharesSum : 0;
    if (direction === 'sqm2pal') {
      const sqm = totalSqmNum * pct;
      const pallets = t.sqmPerPallet > 0 ? sqm / t.sqmPerPallet : 0;
      return { type: t, pct, sqm, pallets };
    } else {
      const pallets = totalPalNum * pct;
      const sqm = pallets * t.sqmPerPallet;
      return { type: t, pct, sqm, pallets };
    }
  }), [types, sharesNum, sharesSum, totalSqmNum, totalPalNum, direction]);

  const totalPallets = breakdown.reduce((s, b) => s + b.pallets, 0);
  const totalSqmOut  = breakdown.reduce((s, b) => s + b.sqm, 0);

  const addType = () => {
    const name = newName.trim();
    const sqm  = num(newSqm);
    if (!name || sqm <= 0) return;
    setTypes(prev => [...prev, { id: `c_${Date.now()}`, name, sqmPerPallet: sqm }]);
    setNewName(''); setNewSqm('');
  };
  const removeType = (id: string) => setTypes(prev => prev.filter(t => t.id !== id || t.builtin));
  const resetDefaults = () => setTypes(DEFAULT_PALLET_TYPES);

  if (!open) return null;

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid #d8dee9',
    borderRadius: 7, fontSize: 13, fontFamily: 'inherit', outline: 'none',
    background: '#fff', color: theme.textPrimary,
  };
  const cell: React.CSSProperties = { padding: '8px 10px', fontSize: 12, color: theme.textPrimary };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,25,55,0.35)',
        zIndex: 1100,
      }} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 460, maxWidth: '95vw',
        background: '#f7f9fc', boxShadow: '-12px 0 40px rgba(15,25,55,0.25)',
        zIndex: 1101, display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid #e5eaf2',
          background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: accent, letterSpacing: 0.6, textTransform: 'uppercase' }}>
              SQM ↔ Pallet Calculator
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: theme.textPrimary, marginTop: 2 }}>
              Mixed Breakdown
            </div>
            {contextLabel && (
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                Context: {contextLabel}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: '#eef2f8', color: theme.textPrimary, width: 32, height: 32,
            borderRadius: 8, cursor: 'pointer', fontSize: 16, fontWeight: 700,
          }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>
          {/* Direction toggle */}
          <div style={{
            display: 'inline-flex', padding: 4, borderRadius: 9, marginBottom: 16,
            background: '#eef2f8', border: '1px solid #e5eaf2', gap: 2,
          }}>
            {(['sqm2pal', 'pal2sqm'] as const).map(d => (
              <button key={d} onClick={() => setDirection(d)} style={{
                padding: '7px 14px', borderRadius: 7, border: 'none',
                background: direction === d ? '#fff' : 'transparent',
                color: direction === d ? accent : theme.textMuted,
                fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
                cursor: 'pointer', fontFamily: 'inherit',
                boxShadow: direction === d ? '0 1px 3px rgba(15,25,55,0.15)' : 'none',
              }}>{d === 'sqm2pal' ? 'SQM → Pallets' : 'Pallets → SQM'}</button>
            ))}
          </div>

          {/* Total input */}
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
            padding: 16, marginBottom: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 6 }}>
              {direction === 'sqm2pal' ? 'Total SQM to convert' : 'Total Pallets to convert'}
            </div>
            {direction === 'sqm2pal' ? (
              <input
                type="number" min={0} value={totalSqm} onChange={e => setTotalSqm(e.target.value)}
                placeholder="e.g. 5000"
                style={{ ...inputStyle, fontSize: 18, fontWeight: 700, padding: '12px 14px' }}
              />
            ) : (
              <input
                type="number" min={0} value={totalPal} onChange={e => setTotalPal(e.target.value)}
                placeholder="e.g. 4000"
                style={{ ...inputStyle, fontSize: 18, fontWeight: 700, padding: '12px 14px' }}
              />
            )}
          </div>

          {/* Type shares */}
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
            padding: 16, marginBottom: 16,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>
                Pallet types & share split
              </div>
              <div style={{ fontSize: 11, color: sharesSum === 100 || sharesSum === 0 ? theme.textMuted : '#d97706', fontWeight: 700 }}>
                Σ share: {sharesSum || 0}{sharesSum > 0 ? ' (auto-normalised)' : ''}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f3f6fb', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase' }}>
                  <th style={{ ...cell, textAlign: 'left' }}>Type</th>
                  <th style={{ ...cell, textAlign: 'right' }}>SQM/Pal</th>
                  <th style={{ ...cell, textAlign: 'right' }}>Share</th>
                  <th style={{ ...cell, textAlign: 'right' }}>SQM</th>
                  <th style={{ ...cell, textAlign: 'right' }}>Pallets</th>
                  <th style={{ ...cell }}></th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map(({ type, pct, sqm, pallets }) => (
                  <tr key={type.id} style={{ borderTop: '1px solid #eef2f8' }}>
                    <td style={{ ...cell, fontWeight: 700 }}>{type.name}</td>
                    <td style={{ ...cell, textAlign: 'right', color: theme.textMuted }}>{type.sqmPerPallet.toFixed(2)}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <input
                        type="number" min={0} value={shares[type.id] ?? ''}
                        onChange={e => setShares(s => ({ ...s, [type.id]: e.target.value }))}
                        placeholder="0"
                        style={{ ...inputStyle, width: 60, padding: '4px 6px', textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ ...cell, textAlign: 'right', color: theme.textMuted }}>{fmtNum(Math.round(sqm))}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: accent }}>{fmtNum(Math.round(pallets))}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      {!type.builtin && (
                        <button onClick={() => removeType(type.id)} title="Remove" style={{
                          border: 'none', background: 'transparent', color: '#dc2626',
                          cursor: 'pointer', fontSize: 14,
                        }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #e5eaf2', background: '#f7f9fc' }}>
                  <td style={{ ...cell, fontWeight: 800 }} colSpan={3}>TOTAL</td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: direction === 'sqm2pal' ? theme.textPrimary : accent }}>
                    {fmtNum(Math.round(direction === 'sqm2pal' ? totalSqmNum : totalSqmOut))}
                  </td>
                  <td style={{ ...cell, textAlign: 'right', fontWeight: 800, color: direction === 'pal2sqm' ? theme.textPrimary : accent }}>
                    {fmtNum(Math.round(direction === 'pal2sqm' ? totalPalNum : totalPallets))}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
            <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>
              Shares are relative weights — they're auto-normalised so they don't need to add up to 100.
              Leave a row at 0 to exclude it.
            </div>
          </div>

          {/* Add custom type */}
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12, padding: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', marginBottom: 10 }}>
              Add custom pallet type
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={newName} onChange={e => setNewName(e.target.value)}
                placeholder="Name (e.g. Type 100)"
                style={{ ...inputStyle, flex: 2 }}
              />
              <input
                type="number" min={0} step={0.01} value={newSqm} onChange={e => setNewSqm(e.target.value)}
                placeholder="SQM/pallet"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button onClick={addType} style={{
                background: accent, color: '#fff', border: 'none', borderRadius: 7,
                padding: '0 14px', fontWeight: 700, fontSize: 12, cursor: 'pointer',
              }}>+ Add</button>
            </div>
            <button onClick={resetDefaults} style={{
              marginTop: 10, background: 'transparent', border: '1px solid #e5eaf2',
              color: theme.textMuted, borderRadius: 7, padding: '6px 12px',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}>Reset to defaults</button>
          </div>
        </div>
      </div>
    </>
  );
};

const SqmCalcButton: React.FC<{ onClick: () => void; accent: string }> = ({ onClick, accent }) => (
  <button onClick={onClick} style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 9,
    border: '1px solid #e5eaf2', background: '#fff', color: accent,
    fontSize: 11, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: 'inherit',
    boxShadow: '0 1px 2px rgba(15,25,55,0.05)',
  }}>📐 SQM Calc</button>
);

export type DashRow = { label: string; value: number; color?: string; sub?: string };

/** Visual summary panel — ranking bars + donut + top tiles. Used by every drill. */
const DrillDashboardView: React.FC<{
  rows: DashRow[];
  accent: string;
  metricLabel: string;
  emptyMessage?: string;
}> = ({ rows, accent, metricLabel, emptyMessage }) => {
  const sorted = useMemo(() => [...rows].filter(r => r.value > 0).sort((a, b) => b.value - a.value), [rows]);
  const total  = useMemo(() => sorted.reduce((s, r) => s + r.value, 0), [sorted]);
  const top    = sorted.slice(0, 10);
  const max    = top.reduce((m, r) => Math.max(m, r.value), 0) || 1;

  if (sorted.length === 0) {
    return (
      <div style={{
        padding: '60px 30px', textAlign: 'center', color: theme.textMuted,
        background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
      }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>📊</div>
        <div style={{ fontWeight: 700, color: theme.textSecondary }}>{emptyMessage || 'No data to visualise'}</div>
      </div>
    );
  }

  // Donut: top 8 + "Other"
  const donutSlices = sorted.length > 8
    ? [...sorted.slice(0, 8), { label: 'Other', value: sorted.slice(8).reduce((s, r) => s + r.value, 0) }]
    : sorted;
  const donutData = donutSlices.map((r, i) => ({ name: r.label, value: r.value, color: r.color || PIE_COLORS[i % PIE_COLORS.length] }));

  // Leader card data
  const leader = sorted[0];
  const leaderShare = total > 0 ? (leader.value / total) * 100 : 0;
  const top3Share   = total > 0 ? (sorted.slice(0, 3).reduce((s, r) => s + r.value, 0) / total) * 100 : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Top stat strip */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{
          flex: 1, minWidth: 220,
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
          color: '#fff', padding: '18px 22px', borderRadius: 14,
          boxShadow: `0 8px 24px ${accent}33`,
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, textTransform: 'uppercase' }}>Leader · {metricLabel}</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, letterSpacing: -0.3 }}>{leader.label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{fmtNum(leader.value)}</div>
          <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.9, marginTop: 2 }}>{leaderShare.toFixed(1)}% of total</div>
        </div>
        <div style={{
          flex: 1, minWidth: 180,
          background: '#fff', border: '1px solid #eef2f8', borderRadius: 14,
          padding: '18px 22px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, letterSpacing: 0.4, textTransform: 'uppercase' }}>Total {metricLabel}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums', marginTop: 6 }}>{fmtNum(total)}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, marginTop: 2 }}>across {sorted.length} {sorted.length === 1 ? 'item' : 'items'}</div>
        </div>
        <div style={{
          flex: 1, minWidth: 180,
          background: '#fff', border: '1px solid #eef2f8', borderRadius: 14,
          padding: '18px 22px',
        }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, letterSpacing: 0.4, textTransform: 'uppercase' }}>Top 3 Concentration</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums', marginTop: 6 }}>{top3Share.toFixed(1)}%</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: theme.textSecondary, marginTop: 2 }}>held by leading 3</div>
        </div>
      </div>

      {/* Bar ranking + Donut side by side */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{
          flex: '2 1 480px', background: '#fff', border: '1px solid #eef2f8',
          borderRadius: 14, padding: '18px 20px',
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.textSecondary, marginBottom: 14, letterSpacing: 0.2 }}>
            Top {Math.min(top.length, 10)} · {metricLabel}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {top.map((r, i) => {
              const pct = (r.value / max) * 100;
              const share = total > 0 ? (r.value / total) * 100 : 0;
              const color = r.color || accent;
              return (
                <div key={r.label + i} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ fontWeight: 700, color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        width: 18, height: 18, borderRadius: 5, background: `${color}1f`, color,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800,
                      }}>{i + 1}</span>
                      {r.label}
                    </span>
                    <span style={{ fontVariantNumeric: 'tabular-nums', color: theme.textSecondary, fontWeight: 700 }}>
                      {fmtNum(r.value)} <span style={{ color: theme.textMuted, fontWeight: 500 }}>· {share.toFixed(1)}%</span>
                    </span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: `linear-gradient(90deg, ${color}, ${color}aa)`,
                      borderRadius: 4,
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{
          flex: '1 1 280px', background: '#fff', border: '1px solid #eef2f8',
          borderRadius: 14, padding: '18px 20px', minHeight: 320,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.textSecondary, marginBottom: 8, letterSpacing: 0.2 }}>
            Distribution
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name"
                cx="50%" cy="50%" innerRadius={50} outerRadius={85}
                paddingAngle={2} stroke="#fff" strokeWidth={2}>
                {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip
                contentStyle={{ background: '#fff', border: '1px solid #e5eaf2', borderRadius: 10, fontSize: 12, fontWeight: 600 }}
                formatter={(v: any) => fmtNum(Number(v))}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {donutData.slice(0, 6).map((d, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>
                <span style={{ width: 9, height: 9, borderRadius: 2, background: d.color }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: theme.textMuted }}>
                  {total > 0 ? ((d.value / total) * 100).toFixed(1) : '0'}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Simple Location Drillout ─────────────── */

type LocDrillProps = {
  accent: string;
  kpiTitle: string;
  rows: WarehouseRow[];
  perWarehouse?: Record<string, WarehouseBreakdown>;
  customerStorage?: any[];
  drill: ReturnType<typeof useDrillStack>;
  onClose: () => void;
};

type LocRow = { key: string; label: string; binCount: number; used: number; total: number };

const LocationDrill: React.FC<LocDrillProps> = ({ accent, kpiTitle, rows, perWarehouse, customerStorage, drill, onClose }) => {
  const { resolve: aliasR } = useAliases();
  const [view, setView] = useState<ViewMode>('table');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lvl = drill.current.level;
  const wh = drill.stack[1]?.key;
  const ar = drill.stack[2]?.key;
  const cap = drill.stack[3]?.key; // segments-per-bin bucket (e.g. "2")

  const tableRows: LocRow[] = useMemo(() => {
    if (lvl === 0) {
      // Warehouses
      if (perWarehouse) {
        return Object.entries(perWarehouse).map(([k, b]) => ({
          key: k, label: aliasR('warehouse', k),
          binCount: b.binCount,
          // Include manual in used / total so manual-only warehouses aren't blank.
          used:  b.totalUsed,
          total: b.binCap  + b.manualCap,
        })).sort((a, b) => b.total - a.total);
      }
      // Fallback from rows
      const map = new Map<string, LocRow>();
      rows.forEach(r => {
        const cur = map.get(r.WAREHOUSE) || { key: r.WAREHOUSE, label: aliasR('warehouse', r.WAREHOUSE), binCount: 0, used: 0, total: 0 };
        cur.binCount += 1;
        cur.used += num(r.USED_BINS);
        cur.total += drillCalcCap(r);
        map.set(r.WAREHOUSE, cur);
      });
      return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }
    if (lvl === 1 && wh) {
      // Areas inside the warehouse — keyed by full STORAGE_AREA (LKORT-LKBER).
      const map = new Map<string, LocRow & { isManual?: boolean }>();
      rows.filter(r => r.WAREHOUSE === wh).forEach(r => {
        const area = String(r.STORAGE_AREA || '').trim();
        const cur = map.get(area) || { key: area, label: getChamberLabel(aliasR, wh, area), binCount: 0, used: 0, total: 0 };
        cur.binCount += 1;
        cur.used += num(r.USED_BINS);
        cur.total += drillCalcCap(r);
        map.set(area, cur);
      });
      // Merge MANUAL storage from perWarehouse[wh].manualByLocation —
      // this is the authoritative map built in OperationsDashboard.buildWhStats
      // using weekly-entries.area_capacity (+ fallback to per-client capacity).
      const manualLocs = (perWarehouse && perWarehouse[wh]?.manualByLocation) || {};
      Object.entries(manualLocs).forEach(([loc, data]: any) => {
        const cap = num(data?.cap);
        const used = num(data?.used);
        if (cap === 0 && used === 0) return;
        const key = `MANUAL__${loc}`;
        const cur = map.get(key) || { key, label: `${loc} (Manual)`, binCount: 0, used: 0, total: 0, isManual: true };
        cur.used += used;
        cur.total += cap;
        cur.isManual = true;
        map.set(key, cur);
      });
      return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }
    if (lvl === 2 && wh && ar) {
      // Capacity buckets: group bins by their TOTAL_BINS (segments per bin).
      // ar is now the LKORT, so match on STORAGE_AREA prefix.
      const matchArea = (r: WarehouseRow) => String(r.STORAGE_AREA || '').trim() === ar;
      const map = new Map<string, LocRow>();
      rows.filter(r => r.WAREHOUSE === wh && matchArea(r)).forEach(r => {
        const segs = num(r.TOTAL_BINS);  // raw seg for grouping
        const k = String(segs);
        const label = `${segs} segment${segs === 1 ? '' : 's'} / bin`;
        const cur = map.get(k) || { key: k, label, binCount: 0, used: 0, total: 0 };
        cur.binCount += 1;
        cur.used += num(r.USED_BINS);
        cur.total += drillCalcCap(r);
        map.set(k, cur);
      });
      return Array.from(map.values()).sort((a, b) => Number(b.key) - Number(a.key));
    }
    if (lvl === 3 && wh && ar && cap) {
      // Pallet types inside the (warehouse, area, capacity bucket)
      const capN = Number(cap);
      const matchArea = (r: WarehouseRow) => String(r.STORAGE_AREA || '').trim() === ar;
      const map = new Map<string, LocRow>();
      rows.filter(r => r.WAREHOUSE === wh && matchArea(r) && num(r.TOTAL_BINS) === capN).forEach(r => {
        const k = r.SHELF_SIZE || '—';
        const cur = map.get(k) || { key: k, label: k, binCount: 0, used: 0, total: 0 };
        cur.binCount += 1;
        cur.used += num(r.USED_BINS);
        cur.total += drillCalcCap(r);
        map.set(k, cur);
      });
      return Array.from(map.values()).sort((a, b) => b.total - a.total);
    }
    return [];
  }, [lvl, wh, ar, cap, rows, perWarehouse, customerStorage]);

  const levelLabel =
    lvl === 0 ? 'Warehouse' :
    lvl === 1 ? 'Area' :
    lvl === 2 ? 'Capacity (segments/bin)' :
    lvl === 3 ? 'Pallet Type' : 'Bin';
  const canDeeper = lvl < 3;

  const grand = useMemo(() => tableRows.reduce(
    (a, r) => ({ binCount: a.binCount + r.binCount, used: a.used + r.used, total: a.total + r.total }),
    { binCount: 0, used: 0, total: 0 }
  ), [tableRows]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(20, 30, 60, 0.45)',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 1100, maxHeight: '88vh',
        background: '#fff', border: '1px solid #e5eaf2', borderRadius: 16,
        boxShadow: '0 40px 100px rgba(15,25,55,0.30)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: theme.fontFamily,
      }}>
        {/* HEADER */}
        <div style={{
          padding: '20px 26px', borderBottom: '1px solid #eef2f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div style={{ width: 4, height: 40, borderRadius: 2, background: accent }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: 0.3 }}>
                {kpiTitle} · {levelLabel}
              </div>
              <h2 style={{ margin: '3px 0 0', fontSize: 19, fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.3 }}>
                {drill.stack.length > 1 ? drill.stack.slice(1).map(s => s.label).join(' › ') : 'All Warehouses'}
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DrillViewToggle view={view} setView={setView} accent={accent} />
            <button onClick={drill.drillBack} disabled={drill.stack.length <= 1}
              style={{
                width: 34, height: 34, borderRadius: 8,
                border: '1px solid #e5eaf2',
                background: drill.stack.length > 1 ? '#fff' : '#fafbfd',
                color: drill.stack.length > 1 ? theme.textPrimary : theme.textMuted,
                cursor: drill.stack.length > 1 ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}>←</button>
            <button onClick={onClose} style={{
              width: 34, height: 34, borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 16, color: theme.textSecondary, fontWeight: 600,
              fontFamily: 'inherit',
            }}>×</button>
          </div>
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24, background: '#fafbfd' }}>
          {view === 'dashboard' ? (
            <DrillDashboardView
              accent={accent}
              metricLabel="Total Segments"
              rows={tableRows.map(r => ({ label: r.label, value: r.total, sub: `${fmtNum(r.used)} used` }))}
            />
          ) : (
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
            overflow: 'hidden', boxShadow: '0 1px 3px rgba(20,40,80,0.04)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 38 }}>#</th>
                  <th style={th}>{levelLabel}</th>
                  <th style={{ ...th, textAlign: 'right' }}>Locations</th>
                  <th style={{ ...th, textAlign: 'right' }}>Used Segments</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total Segments</th>
                  <th style={{ ...th, textAlign: 'right', width: 80 }}>Util %</th>
                  {canDeeper && <th style={{ ...th, width: 50 }}></th>}
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No data</td></tr>
                )}
                {tableRows.map((r, i) => {
                  const util = r.total > 0 ? (r.used / r.total) * 100 : 0;
                  return (
                    <tr key={r.key}
                      style={{ borderBottom: `1px solid ${theme.rowBorder}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7faff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                      <td style={{ ...td, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 700, color: theme.textPrimary }}>{r.label}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary }}>{fmtNum(r.binCount)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary, fontWeight: 600 }}>{fmtNum(r.used)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary, fontWeight: 600 }}>{fmtNum(r.total)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textSecondary, fontWeight: 600 }}>{util.toFixed(1)}%</td>
                      {canDeeper && (
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button
                            onClick={() => drill.drillInto({ level: lvl + 1, label: r.label, key: r.key })}
                            style={{
                              width: 26, height: 26, borderRadius: 6,
                              border: '1px solid #e5eaf2', background: '#fff',
                              color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                              fontFamily: 'inherit',
                            }}>›</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr style={{ background: theme.panelBg }}>
                    <td style={{ ...td, fontWeight: 800, color: theme.textSecondary }} colSpan={2}>TOTAL</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.binCount)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.used)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.total)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary }}>
                      {grand.total > 0 ? ((grand.used / grand.total) * 100).toFixed(1) : '0.0'}%
                    </td>
                    {canDeeper && <td style={td}></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Total Capacity Drillout ───────────────
 * 4-level drill:
 *   L0 → Warehouse list (Rack Cap / Manual Cap / Total / Used / Free / Util%)
 *   L1 → Source split inside picked warehouse: LFS Rack vs Manual
 *   L2 → Area detail
 *          rack   → per LKORT (rackByArea)
 *          manual → per warehouse_area (manualByLocation)
 *   L3 → Leaf
 *          rack   → bins grouped by segments / bin (capacity bucket)
 *          manual → single-row summary
 * Header summary tiles + CSV export.
 * ────────────────────────────────────────────────────── */

type CapDrillProps = {
  accent: string;
  kpiTitle: string;
  rows: WarehouseRow[];
  perWarehouse: Record<string, WarehouseBreakdown>;
  drill: ReturnType<typeof useDrillStack>;
  onClose: () => void;
};

type CapRow = {
  key: string;
  label: string;
  rackCap: number;
  manualCap: number;
  totalCap: number;
  used: number;
  binCount: number;
};

const CapacityDrill: React.FC<CapDrillProps> = ({ accent, kpiTitle, rows, perWarehouse, drill, onClose }) => {
  const { resolve: aliasR } = useAliases();
  const [view, setView] = useState<ViewMode>('table');
  const [sqmOpen, setSqmOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lvl = drill.current.level;
  const wh  = drill.stack[1]?.key;
  const src = drill.stack[2]?.key as 'RACK' | 'MANUAL' | undefined;
  const ar  = drill.stack[3]?.key;

  const tableRows: CapRow[] = useMemo(() => {
    if (lvl === 0) {
      return Object.entries(perWarehouse).map(([k, b]) => ({
        key: k, label: aliasR('warehouse', k),
        rackCap: b.binCap,
        manualCap: b.manualCap,
        totalCap: b.binCap + b.manualCap,
        // Used = all consuming sources: rack + blocked + virtual/bulk + manual + floor + areaBlocked + committed shortfall
        // (b.totalUsed already includes areaBlocked AND commit — do NOT add again)
        used: b.totalUsed,
        binCount: b.binCount,
      })).filter(r => r.totalCap > 0 || r.used > 0).sort((a, b) => b.totalCap - a.totalCap);
    }
    if (lvl === 1 && wh) {
      const b = perWarehouse[wh];
      if (!b) return [];
      const out: CapRow[] = [];
      if (b.binCap > 0) out.push({
        key: 'RACK', label: 'LFS Rack (Physical Bins)',
        rackCap: b.binCap, manualCap: 0, totalCap: b.binCap,
        // Rack source used = rack + blocked + virtual(bulk) + floor + committed (everything non-manual)
        used: b.bin + b.blocked + b.virtual + b.floor + b.commit,
        binCount: b.binCount,
      });
      if (b.manualCap > 0 || b.manual > 0) out.push({
        key: 'MANUAL', label: 'Manual (Weekly Entries)',
        rackCap: 0, manualCap: b.manualCap, totalCap: b.manualCap,
        used: b.manual, binCount: 0,
      });
      return out;
    }
    if (lvl === 2 && wh && src) {
      const b = perWarehouse[wh];
      if (!b) return [];
      if (src === 'RACK') {
        const ra = b.rackByArea || {};
        return Object.entries(ra).map(([area, v]: any) => ({
          key: area, label: getChamberLabel(aliasR, wh, area),
          rackCap: num(v.cap), manualCap: 0, totalCap: num(v.cap),
          used: num(v.used), binCount: num(v.binCount),
        })).sort((a, b) => b.totalCap - a.totalCap);
      }
      const ml = b.manualByLocation || {};
      return Object.entries(ml).map(([area, v]: any) => ({
        key: area, label: getChamberLabel(aliasR, wh, area),
        rackCap: 0, manualCap: num(v.cap), totalCap: num(v.cap),
        used: num(v.used), binCount: 0,
      })).sort((a, b) => b.totalCap - a.totalCap);
    }
    if (lvl === 3 && wh && src && ar) {
      if (src === 'RACK') {
        // Group bins by segments-per-bin (capacity bucket)
        const map = new Map<string, CapRow>();
        rows
          .filter(r => r.WAREHOUSE === wh && String(r.STORAGE_AREA || '').trim() === ar)
          // Exclude temp-blocked bins so Level-3 drill matches the Level-2 tile
          .filter(r => String(r.LPFSTA || '').trim() !== '2' && r.OCCUPANCY_STATUS !== 'TEMP_BLOCKED')
          .forEach(r => {
          const segs = num(r.TOTAL_BINS);
          const k = String(segs);
          const cur = map.get(k) || {
            key: k, label: `${segs} segment${segs === 1 ? '' : 's'} / bin`,
            rackCap: 0, manualCap: 0, totalCap: 0, used: 0, binCount: 0,
          };
          const adjCap = drillCalcCap(r);
          cur.binCount += 1;
          cur.rackCap += adjCap;
          cur.totalCap += adjCap;
          cur.used += num(r.USED_BINS);
          map.set(k, cur);
        });
        return Array.from(map.values()).sort((a, b) => Number(b.key) - Number(a.key));
      }
      // Manual leaf — single summary row
      const v: any = perWarehouse[wh]?.manualByLocation?.[ar];
      if (!v) return [];
      return [{
        key: ar, label: `${ar} (Manual)`,
        rackCap: 0, manualCap: num(v.cap), totalCap: num(v.cap),
        used: num(v.used), binCount: 0,
      }];
    }
    return [];
  }, [lvl, wh, src, ar, rows, perWarehouse]);

  // Grand totals = sum of visible table rows. Header tiles and table
  // footer always show the same numbers so they stay consistent.
  const grand = useMemo(() => tableRows.reduce(
    (a, r) => ({
      rackCap: a.rackCap + r.rackCap,
      manualCap: a.manualCap + r.manualCap,
      totalCap: a.totalCap + r.totalCap,
      used: a.used + r.used,
      binCount: a.binCount + r.binCount,
    }),
    { rackCap: 0, manualCap: 0, totalCap: 0, used: 0, binCount: 0 },
  ), [tableRows]);

  const grandFree = Math.max(0, grand.totalCap - grand.used);
  const grandUtilRaw = grand.totalCap > 0 ? (grand.used / grand.totalCap) * 100 : 0;
  const grandUtil = Math.min(100, grandUtilRaw);

  const levelLabel =
    lvl === 0 ? 'Warehouse' :
    lvl === 1 ? 'Source' :
    lvl === 2 ? 'Area' :
                'Detail';
  const canDeeper = lvl < 3 && !(lvl === 2 && src === 'MANUAL');

  const exportCSV = () => {
    const header = ['#', levelLabel, 'Locations', 'Rack Cap', 'Manual Cap', 'Total Cap', 'Used', 'Free', 'Util %'];
    const lines = [header.join(',')];
    tableRows.forEach((r, i) => {
      const free = Math.max(0, r.totalCap - r.used);
      const util = r.totalCap > 0 ? ((r.used / r.totalCap) * 100).toFixed(1) : '0.0';
      lines.push([i + 1, `"${r.label.replace(/"/g, '""')}"`, r.binCount, r.rackCap, r.manualCap, r.totalCap, r.used, free, util + '%'].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Total_Capacity_${levelLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const tile = (label: string, val: string, color = theme.textPrimary): React.CSSProperties => ({}) as any;
  const Tile: React.FC<{ label: string; val: string; color?: string }> = ({ label, val, color = theme.textPrimary }) => (
    <div style={{
      flex: 1, minWidth: 110,
      background: '#fff', border: '1px solid #eef2f8', borderRadius: 10,
      padding: '10px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: theme.textMuted, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{val}</div>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(20, 30, 60, 0.45)',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 1200, maxHeight: '90vh',
        background: '#fff', border: '1px solid #e5eaf2', borderRadius: 16,
        boxShadow: '0 40px 100px rgba(15,25,55,0.30)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: theme.fontFamily,
      }}>
        {/* HEADER */}
        <div style={{
          padding: '20px 26px', borderBottom: '1px solid #eef2f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div style={{ width: 4, height: 40, borderRadius: 2, background: accent }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: 0.3 }}>
                {kpiTitle} · {levelLabel}
              </div>
              <h2 style={{ margin: '3px 0 0', fontSize: 19, fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {drill.stack.length <= 1 ? 'All Warehouses' : drill.stack.slice(1).map((s, i) => {
                  const lt = ['Warehouse', 'Source', 'Area', 'Detail'][i] || '';
                  return (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {i > 0 && <span style={{ color: theme.textMuted, fontWeight: 400 }}>›</span>}
                      <span style={{
                        display: 'inline-flex', alignItems: 'baseline', gap: 6,
                        padding: '4px 10px', borderRadius: 8,
                        background: `${accent}12`, border: `1px solid ${accent}30`,
                      }}>
                        <span style={{ fontSize: 9, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: 0.4 }}>{lt}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>{s.label}</span>
                      </span>
                    </span>
                  );
                })}
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DrillViewToggle view={view} setView={setView} accent={accent} />
            <SqmCalcButton onClick={() => setSqmOpen(true)} accent={accent} />
            <SqmCalculatorDrawer open={sqmOpen} onClose={() => setSqmOpen(false)} accent={accent} contextLabel={kpiTitle} />
            <button onClick={exportCSV} style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 12, fontWeight: 700, color: theme.textPrimary,
              fontFamily: 'inherit',
            }}>Export CSV</button>
            <button onClick={drill.drillBack} disabled={drill.stack.length <= 1}
              style={{
                width: 34, height: 34, borderRadius: 8,
                border: '1px solid #e5eaf2',
                background: drill.stack.length > 1 ? '#fff' : '#fafbfd',
                color: drill.stack.length > 1 ? theme.textPrimary : theme.textMuted,
                cursor: drill.stack.length > 1 ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}>←</button>
            <button onClick={onClose} style={{
              width: 34, height: 34, borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 16, color: theme.textSecondary, fontWeight: 600,
              fontFamily: 'inherit',
            }}>×</button>
          </div>
        </div>

        {/* SUMMARY TILES */}
        <div style={{
          padding: '14px 24px', background: '#fafbfd',
          borderBottom: '1px solid #eef2f8',
          display: 'flex', gap: 10, flexWrap: 'wrap',
        }}>
          <Tile label="Rack Cap" val={fmtNum(grand.rackCap)} color={theme.accents.blue} />
          <Tile label="Manual Cap" val={fmtNum(grand.manualCap)} color={theme.accents.amber} />
          <Tile label="Total Cap" val={fmtNum(grand.totalCap)} color={accent} />
          <Tile label="Used" val={fmtNum(grand.used)} color={theme.accents.indigo} />
          <Tile label="Free" val={fmtNum(grandFree)} color={theme.accents.green} />
          <Tile label="Util %" val={`${grandUtil.toFixed(1)}%`} color={theme.textPrimary} />
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24, background: '#fafbfd' }}>
          {view === 'dashboard' ? (
            <DrillDashboardView
              accent={accent}
              metricLabel="Total Capacity"
              rows={tableRows.map(r => ({ label: r.label, value: r.totalCap }))}
            />
          ) : (
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
            overflow: 'hidden', boxShadow: '0 1px 3px rgba(20,40,80,0.04)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 38 }}>#</th>
                  <th style={th}>{levelLabel}</th>
                  <th style={{ ...th, textAlign: 'right' }}>Locations</th>
                  <th style={{ ...th, textAlign: 'right' }}>Rack Cap</th>
                  <th style={{ ...th, textAlign: 'right' }}>Manual Cap</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total Cap</th>
                  <th style={{ ...th, textAlign: 'right' }}>Used</th>
                  <th style={{ ...th, textAlign: 'right' }}>Free</th>
                  <th style={{ ...th, textAlign: 'right', width: 80 }}>Util %</th>
                  {canDeeper && <th style={{ ...th, width: 50 }}></th>}
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No data</td></tr>
                )}
                {tableRows.map((r, i) => {
                  const free = Math.max(0, r.totalCap - r.used);
                  const utilRaw = r.totalCap > 0 ? (r.used / r.totalCap) * 100 : 0;
                  const util = Math.min(100, utilRaw);
                  const over = r.used > r.totalCap;
                  return (
                    <tr key={r.key}
                      style={{ borderBottom: `1px solid ${theme.rowBorder}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7faff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                      <td style={{ ...td, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 700, color: theme.textPrimary }}>
                        {r.label}
                        {over && (
                          <span title={`Over-committed by ${fmtNum(r.used - r.totalCap)} pallets`} style={{
                            marginLeft: 8, fontSize: 9, fontWeight: 800, padding: '2px 6px',
                            borderRadius: 4, background: '#fef2f2', color: '#dc2626',
                            border: '1px solid #fecaca', textTransform: 'uppercase', letterSpacing: 0.4,
                          }}>⚠ Overcommit</span>
                        )}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary }}>{fmtNum(r.binCount)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.accents.blue, fontWeight: 600 }}>{fmtNum(r.rackCap)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.accents.amber, fontWeight: 600 }}>{fmtNum(r.manualCap)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary, fontWeight: 700 }}>{fmtNum(r.totalCap)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: over ? '#dc2626' : theme.accents.indigo, fontWeight: 700 }}>{fmtNum(r.used)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.accents.green, fontWeight: 600 }}>{fmtNum(free)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: over ? '#dc2626' : theme.textSecondary, fontWeight: 700 }}>{util.toFixed(1)}%</td>
                      {canDeeper && (
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button
                            onClick={() => drill.drillInto({ level: lvl + 1, label: r.label, key: r.key })}
                            style={{
                              width: 26, height: 26, borderRadius: 6,
                              border: '1px solid #e5eaf2', background: '#fff',
                              color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                              fontFamily: 'inherit',
                            }}>›</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr style={{ background: theme.panelBg }}>
                    <td style={{ ...td, fontWeight: 800, color: theme.textSecondary }} colSpan={2}>TOTAL</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.binCount)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.accents.blue, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.rackCap)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.accents.amber, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.manualCap)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.totalCap)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.accents.indigo, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.used)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.accents.green, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandFree)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary }}>{grandUtil.toFixed(1)}%</td>
                    {canDeeper && <td style={td}></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Occupied Capacity Drillout ───────────────
 * Occupied = Rack Used + Temp Blocked + Area Blocked + Virtual + Floor + Manual + Committed
 * (the 7 components actually consumed/reserved in the warehouse).
 *
 * 3-level drill: Warehouse → Area → Detail
 *   L0 → Warehouse list (one row per wh, columns: BIN, Rack, Temp Blocked,
 *         Area Blocked, Virtual, Floor, Manual, Committed, Total, Share %)
 *   L1 → Area breakdown inside picked warehouse
 *         (aggregates rackByArea + manualByLocation + areaBlockedByArea)
 *   L2 → Component detail inside picked area
 *         (Rack Used, Temp Blocked, Area Blocked, Virtual, Floor, Manual,
 *          Committed as individual rows)
 * ────────────────────────────────────────────────────────── */

type OccDrillProps = {
  accent: string;
  kpiTitle: string;
  rows: WarehouseRow[];
  perWarehouse: Record<string, WarehouseBreakdown>;
  drill: ReturnType<typeof useDrillStack>;
  onClose: () => void;
};

type Component = 'RACK' | 'TEMP_BLOCKED' | 'AREA_BLOCKED' | 'VIRTUAL' | 'FLOOR' | 'MANUAL' | 'COMMITTED';

const COMP_META: Record<Component, { label: string; color: string; help: string }> = {
  RACK:          { label: 'Rack Used',     color: '#2563eb', help: 'IBM physical bins occupied' },
  TEMP_BLOCKED:  { label: 'Temp Blocked',  color: '#dc2626', help: 'Bins flagged TEMP_BLOCKED (LPFSTA=2)' },
  AREA_BLOCKED:  { label: 'Area Blocked',  color: '#be123c', help: 'Bins manually blocked by LGPGM039 program' },
  VIRTUAL:       { label: 'Virtual',       color: '#7c3aed', help: 'Pallets in virtual / non-physical bins' },
  FLOOR:         { label: 'Floor',         color: '#0891b2', help: 'Floor pallets (residual: IBM total − rack − blocked)' },
  MANUAL:        { label: 'Manual',        color: '#d97706', help: 'Manual storage pallets (weekly entries)' },
  COMMITTED:     { label: 'Committed',     color: '#16a34a', help: 'Customer contract minimum commitments' },
};

type OccRow = {
  key: string;
  label: string;
  binCount: number;
  rack: number;
  tempBlocked: number;
  areaBlocked: number;
  virtual: number;
  floor: number;
  manual: number;
  committed: number;
  total: number;
  note?: string;
};

const sumOcc = (b: any): OccRow => {
  const rack = num(b.bin);
  const tempBlocked = num(b.tempBlocked);
  const areaBlocked = num(b.areaBlocked);
  const virtual = num(b.virtual);
  const floor = num(b.floor);
  const manual = num(b.manual);
  const committed = num(b.commit);
  return {
    key: '', label: '', binCount: num(b.binCount),
    rack, tempBlocked, areaBlocked, virtual, floor, manual, committed,
    total: rack + tempBlocked + areaBlocked + virtual + floor + manual + committed,
  };
};

const OccupiedDrill: React.FC<OccDrillProps> = ({ accent, kpiTitle, rows, perWarehouse, drill, onClose }) => {
  const { resolve: aliasR } = useAliases();
  const [view, setView] = useState<ViewMode>('table');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* 3-level drill: Warehouse → Area → Detail
   * L0 = all warehouses
   * L1 = areas inside selected warehouse
   * L2 = component detail inside selected area
   */
  const lvl  = drill.current.level;
  const wh   = drill.stack[1]?.key;
  const area = drill.stack[2]?.key;

  const tableRows: OccRow[] = useMemo(() => {
    // L0: Warehouse rows — one row per warehouse with all component columns
    if (lvl === 0) {
      return Object.entries(perWarehouse).map(([k, b]: any) => {
        const o = sumOcc(b);
        return { ...o, key: k, label: aliasR('warehouse', k) };
      }).filter(r => r.total > 0).sort((a, b) => b.total - a.total);
    }

    // L1: Area rows inside selected warehouse
    if (lvl === 1 && wh) {
      const b: any = perWarehouse[wh];
      if (!b) return [];
      const rba = b.rackByArea || {};
      const mbl = b.manualByLocation || {};
      const aba = (b.areaBlockedByArea || []) as any[];

      // Collect all area keys
      const areaKeys = new Set<string>();
      Object.keys(rba).forEach(k => areaKeys.add(k));
      Object.keys(mbl).forEach(k => areaKeys.add(k));
      aba.forEach((r: any) => { if (r.AREA) areaKeys.add(r.AREA); });

      return Array.from(areaKeys).map(aKey => {
        const rv = rba[aKey] || { cap: 0, used: 0, binCount: 0 };
        const mv = mbl[aKey] || { cap: 0, used: 0 };
        const areaBlockedCount = aba.filter((r: any) => r.AREA === aKey).reduce((s: number, r: any) => s + num(r.COUNT || r.BLOCKED || 1), 0);
        // Temp blocked is proportionally distributed based on rack area ratio
        const totalRackBins = Object.values(rba).reduce((s: number, v: any) => s + num(v.binCount), 0);
        const areaTempBlocked = totalRackBins > 0 ? Math.round(num(b.tempBlocked) * num(rv.binCount) / totalRackBins) : 0;
        // Virtual and floor proportionally distributed too
        const totalRackUsed = Object.values(rba).reduce((s: number, v: any) => s + num(v.used), 0);
        const ratio = totalRackUsed > 0 ? num(rv.used) / totalRackUsed : 0;
        const areaVirtual = Math.round(num(b.virtual) * ratio);
        const areaFloor = Math.round(num(b.floor) * ratio);
        const areaCommit = Math.round(num(b.commit) * ratio);
        const rack = num(rv.used);
        const manual = num(mv.used);
        const total = rack + areaTempBlocked + areaBlockedCount + areaVirtual + areaFloor + manual + areaCommit;

        return {
          key: aKey, label: getChamberLabel(aliasR, wh, aKey),
          binCount: num(rv.binCount),
          rack,
          tempBlocked: areaTempBlocked,
          areaBlocked: areaBlockedCount,
          virtual: areaVirtual,
          floor: areaFloor,
          manual,
          committed: areaCommit,
          total,
        };
      }).filter(r => r.total > 0).sort((a, b) => b.total - a.total);
    }

    // L2: Component detail inside selected area
    if (lvl === 2 && wh && area) {
      const b: any = perWarehouse[wh];
      if (!b) return [];
      const rba = b.rackByArea || {};
      const mbl = b.manualByLocation || {};
      const aba = (b.areaBlockedByArea || []) as any[];

      const rv = rba[area] || { cap: 0, used: 0, binCount: 0 };
      const mv = mbl[area] || { cap: 0, used: 0 };
      const areaBlockedCount = aba.filter((r: any) => r.AREA === area).reduce((s: number, r: any) => s + num(r.COUNT || r.BLOCKED || 1), 0);
      const totalRackBins = Object.values(rba).reduce((s: number, v: any) => s + num(v.binCount), 0);
      const areaTempBlocked = totalRackBins > 0 ? Math.round(num(b.tempBlocked) * num(rv.binCount) / totalRackBins) : 0;
      const totalRackUsed = Object.values(rba).reduce((s: number, v: any) => s + num(v.used), 0);
      const ratio = totalRackUsed > 0 ? num(rv.used) / totalRackUsed : 0;
      const areaVirtual = Math.round(num(b.virtual) * ratio);
      const areaFloor = Math.round(num(b.floor) * ratio);
      const areaCommit = Math.round(num(b.commit) * ratio);

      const blank: OccRow = { key: '', label: '', binCount: 0, rack: 0, tempBlocked: 0, areaBlocked: 0, virtual: 0, floor: 0, manual: 0, committed: 0, total: 0 };
      const details: OccRow[] = [];
      const rackVal = num(rv.used);
      if (rackVal > 0) details.push({ ...blank, key: 'RACK', label: COMP_META.RACK.label, note: COMP_META.RACK.help, binCount: num(rv.binCount), rack: rackVal, total: rackVal });
      if (areaTempBlocked > 0) details.push({ ...blank, key: 'TEMP_BLOCKED', label: COMP_META.TEMP_BLOCKED.label, note: COMP_META.TEMP_BLOCKED.help, tempBlocked: areaTempBlocked, total: areaTempBlocked });
      if (areaBlockedCount > 0) details.push({ ...blank, key: 'AREA_BLOCKED', label: COMP_META.AREA_BLOCKED.label, note: COMP_META.AREA_BLOCKED.help, areaBlocked: areaBlockedCount, total: areaBlockedCount });
      if (areaVirtual > 0) details.push({ ...blank, key: 'VIRTUAL', label: COMP_META.VIRTUAL.label, note: COMP_META.VIRTUAL.help, virtual: areaVirtual, total: areaVirtual });
      if (areaFloor > 0) details.push({ ...blank, key: 'FLOOR', label: COMP_META.FLOOR.label, note: COMP_META.FLOOR.help, floor: areaFloor, total: areaFloor });
      const manualVal = num(mv.used);
      if (manualVal > 0) details.push({ ...blank, key: 'MANUAL', label: COMP_META.MANUAL.label, note: COMP_META.MANUAL.help, manual: manualVal, total: manualVal });
      if (areaCommit > 0) details.push({ ...blank, key: 'COMMITTED', label: COMP_META.COMMITTED.label, note: COMP_META.COMMITTED.help, committed: areaCommit, total: areaCommit });
      return details;
    }
    return [];
  }, [lvl, wh, area, rows, perWarehouse, aliasR]);

  const grand = useMemo(() => tableRows.reduce(
    (a, r) => ({
      binCount: a.binCount + r.binCount,
      rack: a.rack + r.rack,
      tempBlocked: a.tempBlocked + r.tempBlocked,
      areaBlocked: a.areaBlocked + r.areaBlocked,
      virtual: a.virtual + r.virtual,
      floor: a.floor + r.floor,
      manual: a.manual + r.manual,
      committed: a.committed + r.committed,
      total: a.total + r.total,
    }),
    { binCount: 0, rack: 0, tempBlocked: 0, areaBlocked: 0, virtual: 0, floor: 0, manual: 0, committed: 0, total: 0 },
  ), [tableRows]);

  const levelLabel =
    lvl === 0 ? 'Warehouse' :
    lvl === 1 ? 'Area' :
                'Detail';
  const canDeeper = lvl < 2;

  const exportCSV = () => {
    const header = ['#', levelLabel, 'Locations', 'Used Rack', 'Temp Blocked', 'Area Blocked', 'Virtual', 'Floor', 'Manual', 'Committed', 'Total', 'Share %'];
    const lines = [header.join(',')];
    tableRows.forEach((r, i) => {
      const share = grand.total > 0 ? ((r.total / grand.total) * 100).toFixed(1) : '0.0';
      lines.push([i + 1, `"${r.label.replace(/"/g, '""')}"`, r.binCount, r.rack, r.tempBlocked, r.areaBlocked, r.virtual, r.floor, r.manual, r.committed, r.total, share + '%'].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Occupied_Capacity_${levelLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Tile: React.FC<{ label: string; val: string; color?: string; help?: string }> = ({ label, val, color = theme.textPrimary, help }) => (
    <div title={help} style={{
      flex: 1, minWidth: 110,
      background: '#fff', border: '1px solid #eef2f8', borderRadius: 10,
      padding: '10px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{val}</div>
    </div>
  );

  const BAND_KEYS: { k: keyof typeof grand; comp: Component }[] = [
    { k: 'rack', comp: 'RACK' },
    { k: 'tempBlocked', comp: 'TEMP_BLOCKED' },
    { k: 'areaBlocked', comp: 'AREA_BLOCKED' },
    { k: 'virtual', comp: 'VIRTUAL' },
    { k: 'floor', comp: 'FLOOR' },
    { k: 'manual', comp: 'MANUAL' },
    { k: 'committed', comp: 'COMMITTED' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(20, 30, 60, 0.45)',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 1380, maxHeight: '92vh',
        background: '#fff', border: '1px solid #e5eaf2', borderRadius: 16,
        boxShadow: '0 40px 100px rgba(15,25,55,0.30)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: theme.fontFamily,
      }}>
        {/* HEADER */}
        <div style={{
          padding: '20px 26px', borderBottom: '1px solid #eef2f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div style={{ width: 4, height: 40, borderRadius: 2, background: accent }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: 0.3 }}>
                {kpiTitle} · {levelLabel}
              </div>
              <h2 style={{ margin: '3px 0 0', fontSize: 19, fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {drill.stack.length <= 1 ? 'All Warehouses' : drill.stack.slice(1).map((s, i) => {
                  const lt = ['Warehouse', 'Area', 'Detail'][i] || '';
                  return (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {i > 0 && <span style={{ color: theme.textMuted, fontWeight: 400 }}>›</span>}
                      <span style={{
                        display: 'inline-flex', alignItems: 'baseline', gap: 6,
                        padding: '4px 10px', borderRadius: 8,
                        background: `${accent}12`, border: `1px solid ${accent}30`,
                      }}>
                        <span style={{ fontSize: 9, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: 0.4 }}>{lt}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>{s.label}</span>
                      </span>
                    </span>
                  );
                })}
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DrillViewToggle view={view} setView={setView} accent={accent} />
            <button onClick={exportCSV} style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 12, fontWeight: 700, color: theme.textPrimary,
              fontFamily: 'inherit',
            }}>Export CSV</button>
            <button onClick={drill.drillBack} disabled={drill.stack.length <= 1}
              style={{
                width: 34, height: 34, borderRadius: 8,
                border: '1px solid #e5eaf2',
                background: drill.stack.length > 1 ? '#fff' : '#fafbfd',
                color: drill.stack.length > 1 ? theme.textPrimary : theme.textMuted,
                cursor: drill.stack.length > 1 ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}>←</button>
            <button onClick={onClose} style={{
              width: 34, height: 34, borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 16, color: theme.textSecondary, fontWeight: 600,
              fontFamily: 'inherit',
            }}>×</button>
          </div>
        </div>

        {/* SUMMARY TILES */}
        <div style={{
          padding: '14px 24px', background: '#fafbfd',
          borderBottom: '1px solid #eef2f8',
          display: 'flex', gap: 10, flexWrap: 'wrap',
        }}>
          <Tile label="Used Rack"    val={fmtNum(grand.rack)}         color={COMP_META.RACK.color}         help={COMP_META.RACK.help} />
          <Tile label="Temp Blocked" val={fmtNum(grand.tempBlocked)}  color={COMP_META.TEMP_BLOCKED.color} help={COMP_META.TEMP_BLOCKED.help} />
          <Tile label="Area Blocked" val={fmtNum(grand.areaBlocked)}  color={COMP_META.AREA_BLOCKED.color} help={COMP_META.AREA_BLOCKED.help} />
          <Tile label="Virtual"      val={fmtNum(grand.virtual)}      color={COMP_META.VIRTUAL.color}      help={COMP_META.VIRTUAL.help} />
          <Tile label="Floor"        val={fmtNum(grand.floor)}        color={COMP_META.FLOOR.color}        help={COMP_META.FLOOR.help} />
          <Tile label="Manual"       val={fmtNum(grand.manual)}       color={COMP_META.MANUAL.color}       help={COMP_META.MANUAL.help} />
          <Tile label="Committed"    val={fmtNum(grand.committed)}    color={COMP_META.COMMITTED.color}    help={COMP_META.COMMITTED.help} />
          <Tile label="Total Occupied" val={fmtNum(grand.total)} color={accent} />
        </div>

        {/* STACKED BAR LEGEND BAND */}
        {grand.total > 0 && (
          <div style={{ padding: '8px 24px 14px', background: '#fafbfd' }}>
            <div style={{
              display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden',
              border: '1px solid #eef2f8', background: '#fff',
            }}>
              {BAND_KEYS.map(({ k, comp }) => {
                const v = grand[k] as number;
                if (v <= 0) return null;
                const meta = COMP_META[comp];
                const pct = (v / grand.total) * 100;
                return (
                  <div key={k} title={`${meta.label}: ${fmtNum(v)} (${pct.toFixed(1)}%)`}
                    style={{ width: `${pct}%`, height: '100%', background: meta.color }} />
                );
              })}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
              {BAND_KEYS.map(({ k, comp }) => {
                const v = grand[k] as number;
                if (v <= 0) return null;
                const meta = COMP_META[comp];
                return (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: theme.textSecondary, fontWeight: 600 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: meta.color }} />
                    {meta.label} · {fmtNum(v)} ({((v / grand.total) * 100).toFixed(1)}%)
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px', background: '#fafbfd' }}>
          {view === 'dashboard' ? (
            <DrillDashboardView
              accent={accent}
              metricLabel="Occupied Pallets"
              rows={tableRows.map(r => ({ label: r.label, value: r.total, color: lvl === 2 ? COMP_META[r.key as Component]?.color : undefined }))}
            />
          ) : (
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
            overflow: 'hidden', boxShadow: '0 1px 3px rgba(20,40,80,0.04)',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 38 }}>#</th>
                  <th style={th}>{levelLabel}</th>
                  <th style={{ ...th, textAlign: 'right' }}>Locations</th>
                  <th style={{ ...th, textAlign: 'right' }}>Used Rack</th>
                  <th style={{ ...th, textAlign: 'right' }}>Temp Blocked</th>
                  <th style={{ ...th, textAlign: 'right' }}>Area Blocked</th>
                  <th style={{ ...th, textAlign: 'right' }}>Virtual</th>
                  <th style={{ ...th, textAlign: 'right' }}>Floor</th>
                  <th style={{ ...th, textAlign: 'right' }}>Manual</th>
                  <th style={{ ...th, textAlign: 'right' }}>Committed</th>
                  <th style={{ ...th, textAlign: 'right' }}>Total</th>
                  <th style={{ ...th, textAlign: 'right', width: 90 }}>Share %</th>
                  {canDeeper && <th style={{ ...th, width: 50 }}></th>}
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 && (
                  <tr><td colSpan={14} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No data</td></tr>
                )}
                {tableRows.map((r, i) => {
                  const share = grand.total > 0 ? (r.total / grand.total) * 100 : 0;
                  const rowAccent = lvl === 2 ? COMP_META[r.key as Component]?.color : undefined;
                  return (
                    <tr key={r.key}
                      style={{ borderBottom: `1px solid ${theme.rowBorder}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7faff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                      <td style={{ ...td, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 700, color: theme.textPrimary }}>
                        {rowAccent && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: rowAccent, marginRight: 8, verticalAlign: 'middle' }} />}
                        {r.label}
                        {r.note && <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500, marginTop: 2 }}>{r.note}</div>}
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.binCount > 0 ? theme.textPrimary : theme.textMuted, fontWeight: r.binCount > 0 ? 600 : 400 }}>{r.binCount > 0 ? fmtNum(r.binCount) : '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.rack > 0 ? COMP_META.RACK.color : theme.textMuted, fontWeight: r.rack > 0 ? 700 : 400 }}>{fmtNum(r.rack)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.tempBlocked > 0 ? COMP_META.TEMP_BLOCKED.color : theme.textMuted, fontWeight: r.tempBlocked > 0 ? 700 : 400 }}>{fmtNum(r.tempBlocked)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.areaBlocked > 0 ? COMP_META.AREA_BLOCKED.color : theme.textMuted, fontWeight: r.areaBlocked > 0 ? 700 : 400 }}>{fmtNum(r.areaBlocked)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.virtual > 0 ? COMP_META.VIRTUAL.color : theme.textMuted, fontWeight: r.virtual > 0 ? 700 : 400 }}>{fmtNum(r.virtual)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.floor > 0 ? COMP_META.FLOOR.color : theme.textMuted, fontWeight: r.floor > 0 ? 700 : 400 }}>{fmtNum(r.floor)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.manual > 0 ? COMP_META.MANUAL.color : theme.textMuted, fontWeight: r.manual > 0 ? 700 : 400 }}>{fmtNum(r.manual)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.committed > 0 ? COMP_META.COMMITTED.color : theme.textMuted, fontWeight: r.committed > 0 ? 700 : 400 }}>{fmtNum(r.committed)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary, fontWeight: 800 }}>{fmtNum(r.total)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                          <div style={{ width: 60, height: 6, background: '#eef2f8', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${share}%`, height: '100%', background: accent }} />
                          </div>
                          <span style={{ fontVariantNumeric: 'tabular-nums', color: theme.textSecondary, fontWeight: 600, minWidth: 38, textAlign: 'right' }}>{share.toFixed(1)}%</span>
                        </div>
                      </td>
                      {canDeeper && (
                        <td style={{ ...td, textAlign: 'right' }}>
                          <button
                            onClick={() => drill.drillInto({ level: lvl + 1, label: r.label, key: r.key })}
                            style={{
                              width: 26, height: 26, borderRadius: 6,
                              border: '1px solid #e5eaf2', background: '#fff',
                              color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                              fontFamily: 'inherit',
                            }}>›</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {tableRows.length > 0 && (
                <tfoot>
                  <tr style={{ background: theme.panelBg }}>
                    <td style={{ ...td, fontWeight: 800, color: theme.textSecondary }} colSpan={2}>TOTAL</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.binCount)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.RACK.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.rack)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.TEMP_BLOCKED.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.tempBlocked)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.AREA_BLOCKED.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.areaBlocked)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.VIRTUAL.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.virtual)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.FLOOR.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.floor)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.MANUAL.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.manual)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: COMP_META.COMMITTED.color, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.committed)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grand.total)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary }}>100%</td>
                    {canDeeper && <td style={td}></td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─────────────── Commitment (Customer Shortfall) Drillout ───────────────
 * Committed Capacity = Σ shortfall of all customer minimum commitments.
 *   shortfall = max(0, minPallets − actualPalletsForCustomer)
 *
 * Two-level drill:
 *   L0 → Warehouse list (one row per warehouse / 'All' bucket)
 *           Customers · Min Pallets · Actual · Shortfall · Met
 *   L1 → Customer detail inside the picked warehouse
 *           Customer · Type · Value · ×Factor · Min Pallets · Actual · Shortfall · Status
 *
 * Header summary tiles + colour-coded status pills + CSV export.
 * ────────────────────────────────────────────────────────── */

type CmDrillProps = {
  accent: string;
  kpiTitle: string;
  commitments: CommitmentRow[];
  drill: ReturnType<typeof useDrillStack>;
  onClose: () => void;
};

const CommitmentDrill: React.FC<CmDrillProps> = ({ accent, kpiTitle, commitments, drill, onClose }) => {
  const { aliases, resolve: aliasR } = useAliases();
  const [view, setView] = useState<ViewMode>('table');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const lvl = drill.current.level;
  const wh  = drill.stack[1]?.key;

  // Group contracts per warehouse bucket. A contract may apply to multiple
  // warehouses or 'All' — split it so each appears in the right bucket but
  // only with its proportional share of the shortfall.
  const byWarehouse = useMemo(() => {
    const m: Record<string, CommitmentRow[]> = {};
    commitments.forEach(c => {
      const targets = c.warehouses && c.warehouses.length ? c.warehouses : ['All'];
      const share = targets.length;
      targets.forEach(w => {
        const split: CommitmentRow = {
          ...c,
          warehouse: w,
          minPallets: Math.ceil(c.minPallets / share),
          actual: Math.ceil(c.actual / share),
          shortfall: Math.ceil(c.shortfall / share),
        };
        (m[w] ||= []).push(split);
      });
    });
    return m;
  }, [commitments]);

  type WhRow = { key: string; label: string; customers: number; minPallets: number; actual: number; shortfall: number; metCount: number };
  const tableRowsL0: WhRow[] = useMemo(() => {
    return Object.entries(byWarehouse).map(([w, rows]) => {
      const minPallets = rows.reduce((s, r) => s + r.minPallets, 0);
      const actual     = rows.reduce((s, r) => s + r.actual, 0);
      const shortfall  = rows.reduce((s, r) => s + r.shortfall, 0);
      const metCount   = rows.filter(r => r.shortfall === 0).length;
      return { key: w, label: aliasR('warehouse', w), customers: rows.length, minPallets, actual, shortfall, metCount };
    }).sort((a, b) => b.shortfall - a.shortfall);
  }, [byWarehouse]);

  const tableRowsL1: CommitmentRow[] = useMemo(() => {
    if (!wh) return [];
    return [...(byWarehouse[wh] || [])].sort((a, b) => b.shortfall - a.shortfall || (aliasR('customer', a.customer)).localeCompare(aliasR('customer', b.customer)));
  }, [byWarehouse, wh]);

  const grandL0 = useMemo(() => tableRowsL0.reduce(
    (a, r) => ({
      customers: a.customers + r.customers,
      minPallets: a.minPallets + r.minPallets,
      actual: a.actual + r.actual,
      shortfall: a.shortfall + r.shortfall,
      metCount: a.metCount + r.metCount,
    }),
    { customers: 0, minPallets: 0, actual: 0, shortfall: 0, metCount: 0 },
  ), [tableRowsL0]);

  const grandL1 = useMemo(() => tableRowsL1.reduce(
    (a, r) => ({
      minPallets: a.minPallets + r.minPallets,
      actual: a.actual + r.actual,
      shortfall: a.shortfall + r.shortfall,
    }),
    { minPallets: 0, actual: 0, shortfall: 0 },
  ), [tableRowsL1]);

  const levelLabel = lvl === 0 ? 'Warehouse' : 'Customer';
  const canDeeper = lvl < 1;

  const exportCSV = () => {
    const lines: string[] = [];
    if (lvl === 0) {
      lines.push(['#', 'Warehouse', 'Customers', 'Min Pallets', 'Actual', 'Shortfall', 'Met'].join(','));
      tableRowsL0.forEach((r, i) => {
        lines.push([i + 1, `"${r.label}"`, r.customers, r.minPallets, r.actual, r.shortfall, `${r.metCount}/${r.customers}`].join(','));
      });
    } else {
      lines.push(['#', 'Customer', 'Original Code', 'Type', 'Value', 'Factor', 'Min Pallets', 'Actual', 'Shortfall', 'Status', 'Notes'].join(','));
      tableRowsL1.forEach((r, i) => {
        const displayName = aliasR('customer', r.customer);
        lines.push([i + 1, `"${displayName}"`, `"${r.customer}"`, r.type, r.value, r.factor, r.minPallets, r.actual, r.shortfall, r.isMet ? 'Met' : 'Short', `"${(r.notes || '').replace(/"/g, '""')}"`].join(','));
      });
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Committed_${levelLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const Tile: React.FC<{ label: string; val: string; color?: string }> = ({ label, val, color = theme.textPrimary }) => (
    <div style={{
      flex: 1, minWidth: 110,
      background: '#fff', border: '1px solid #eef2f8', borderRadius: 10,
      padding: '10px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.textMuted, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{val}</div>
    </div>
  );

  const StatusPill: React.FC<{ met: boolean; label?: string }> = ({ met, label }) => (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px', borderRadius: 999,
      fontSize: 11, fontWeight: 700,
      color: met ? '#15803d' : '#b91c1c',
      background: met ? '#dcfce7' : '#fee2e2',
      border: `1px solid ${met ? '#86efac' : '#fca5a5'}`,
    }}>{label || (met ? 'Met' : 'Short')}</span>
  );

  const empty = commitments.length === 0;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'rgba(20, 30, 60, 0.45)',
      backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 1280, maxHeight: '92vh',
        background: '#fff', border: '1px solid #e5eaf2', borderRadius: 16,
        boxShadow: '0 40px 100px rgba(15,25,55,0.30)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: theme.fontFamily,
      }}>
        {/* HEADER */}
        <div style={{
          padding: '20px 26px', borderBottom: '1px solid #eef2f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
            <div style={{ width: 4, height: 40, borderRadius: 2, background: accent }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: theme.textMuted, letterSpacing: 0.3 }}>
                {kpiTitle} · Customer Shortfall · {levelLabel}
              </div>
              <h2 style={{ margin: '3px 0 0', fontSize: 19, fontWeight: 700, color: theme.textPrimary, letterSpacing: -0.3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {drill.stack.length <= 1 ? 'All Warehouses' : drill.stack.slice(1).map((s, i) => {
                  const lt = ['Warehouse', 'Customer'][i] || '';
                  return (
                    <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      {i > 0 && <span style={{ color: theme.textMuted, fontWeight: 400 }}>›</span>}
                      <span style={{
                        display: 'inline-flex', alignItems: 'baseline', gap: 6,
                        padding: '4px 10px', borderRadius: 8,
                        background: `${accent}12`, border: `1px solid ${accent}30`,
                      }}>
                        <span style={{ fontSize: 9, fontWeight: 700, color: accent, textTransform: 'uppercase', letterSpacing: 0.4 }}>{lt}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>{s.label}</span>
                      </span>
                    </span>
                  );
                })}
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DrillViewToggle view={view} setView={setView} accent={accent} />
            <button onClick={exportCSV} style={{
              padding: '8px 14px', borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 12, fontWeight: 700, color: theme.textPrimary,
              fontFamily: 'inherit',
            }}>Export CSV</button>
            <button onClick={drill.drillBack} disabled={drill.stack.length <= 1}
              style={{
                width: 34, height: 34, borderRadius: 8,
                border: '1px solid #e5eaf2',
                background: drill.stack.length > 1 ? '#fff' : '#fafbfd',
                color: drill.stack.length > 1 ? theme.textPrimary : theme.textMuted,
                cursor: drill.stack.length > 1 ? 'pointer' : 'not-allowed',
                fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              }}>←</button>
            <button onClick={onClose} style={{
              width: 34, height: 34, borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              cursor: 'pointer', fontSize: 16, color: theme.textSecondary, fontWeight: 600,
              fontFamily: 'inherit',
            }}>×</button>
          </div>
        </div>

        {/* SUMMARY TILES */}
        <div style={{
          padding: '14px 24px', background: '#fafbfd',
          borderBottom: '1px solid #eef2f8',
          display: 'flex', gap: 10, flexWrap: 'wrap',
        }}>
          {lvl === 0 ? (
            <>
              <Tile label="Warehouses"   val={fmtNum(tableRowsL0.length)} />
              <Tile label="Contracts"    val={fmtNum(grandL0.customers)} />
              <Tile label="Min Pallets"  val={fmtNum(grandL0.minPallets)} color="#2563eb" />
              <Tile label="Actual"       val={fmtNum(grandL0.actual)} color="#16a34a" />
              <Tile label="Shortfall"    val={fmtNum(grandL0.shortfall)} color="#dc2626" />
              <Tile label="Met"          val={`${grandL0.metCount}/${grandL0.customers}`} color={accent} />
            </>
          ) : (
            <>
              <Tile label="Customers"    val={fmtNum(tableRowsL1.length)} />
              <Tile label="Min Pallets"  val={fmtNum(grandL1.minPallets)} color="#2563eb" />
              <Tile label="Actual"       val={fmtNum(grandL1.actual)} color="#16a34a" />
              <Tile label="Shortfall"    val={fmtNum(grandL1.shortfall)} color="#dc2626" />
              <Tile label="Fulfilment"   val={`${grandL1.minPallets > 0 ? Math.min(100, (grandL1.actual / grandL1.minPallets) * 100).toFixed(1) : '0.0'}%`} color={accent} />
            </>
          )}
        </div>

        {/* CONTENT */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24, background: '#fafbfd' }}>
          {view === 'dashboard' && !empty ? (
            <DrillDashboardView
              accent={accent}
              metricLabel="Shortfall (Pallets)"
              emptyMessage="All commitments are met"
              rows={lvl === 0
                ? tableRowsL0.map(r => ({ label: r.label, value: r.shortfall }))
                : tableRowsL1.map(r => ({ label: aliasR('customer', r.customer) || '—', value: r.shortfall }))}
            />
          ) : (
          <div style={{
            background: '#fff', border: '1px solid #eef2f8', borderRadius: 12,
            overflow: 'hidden', boxShadow: '0 1px 3px rgba(20,40,80,0.04)',
          }}>
            {empty ? (
              <div style={{ padding: '60px 30px', textAlign: 'center', color: theme.textMuted }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                <div style={{ fontWeight: 700, color: theme.textSecondary, marginBottom: 4 }}>No customer commitments yet</div>
                <div style={{ fontSize: 12 }}>Add minimum-pallet contracts in Executive Dashboard → Customer Commitments.</div>
              </div>
            ) : lvl === 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 38 }}>#</th>
                    <th style={th}>Warehouse</th>
                    <th style={{ ...th, textAlign: 'right' }}>Contracts</th>
                    <th style={{ ...th, textAlign: 'right' }}>Min Pallets</th>
                    <th style={{ ...th, textAlign: 'right' }}>Actual</th>
                    <th style={{ ...th, textAlign: 'right' }}>Shortfall</th>
                    <th style={{ ...th, textAlign: 'right' }}>Fulfilment</th>
                    {canDeeper && <th style={{ ...th, width: 50 }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {tableRowsL0.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No data</td></tr>
                  )}
                  {tableRowsL0.map((r, i) => {
                    const fulfil = r.minPallets > 0 ? Math.min(100, (r.actual / r.minPallets) * 100) : 0;
                    return (
                      <tr key={r.key}
                        style={{ borderBottom: `1px solid ${theme.rowBorder}` }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#f7faff')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                        <td style={{ ...td, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                        <td style={{ ...td, fontWeight: 700, color: theme.textPrimary }}>
                          {r.label}
                          <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500, marginTop: 2 }}>{r.metCount}/{r.customers} commitments met</div>
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary }}>{fmtNum(r.customers)}</td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#2563eb', fontWeight: 700 }}>{fmtNum(r.minPallets)}</td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#16a34a', fontWeight: 700 }}>{fmtNum(r.actual)}</td>
                        <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.shortfall > 0 ? '#dc2626' : '#16a34a', fontWeight: 800 }}>{r.shortfall > 0 ? `+${fmtNum(r.shortfall)}` : '0'}</td>
                        <td style={{ ...td, textAlign: 'right' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                            <div style={{ width: 70, height: 6, background: '#eef2f8', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{ width: `${fulfil}%`, height: '100%', background: fulfil >= 100 ? '#16a34a' : '#f59e0b' }} />
                            </div>
                            <span style={{ fontVariantNumeric: 'tabular-nums', color: theme.textSecondary, fontWeight: 600, minWidth: 38, textAlign: 'right' }}>{fulfil.toFixed(0)}%</span>
                          </div>
                        </td>
                        {canDeeper && (
                          <td style={{ ...td, textAlign: 'right' }}>
                            <button
                              onClick={() => drill.drillInto({ level: 1, label: r.label, key: r.key })}
                              style={{
                                width: 26, height: 26, borderRadius: 6,
                                border: '1px solid #e5eaf2', background: '#fff',
                                color: accent, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                                fontFamily: 'inherit',
                              }}>›</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {tableRowsL0.length > 0 && (
                  <tfoot>
                    <tr style={{ background: theme.panelBg }}>
                      <td style={{ ...td, fontWeight: 800, color: theme.textSecondary }} colSpan={2}>TOTAL</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL0.customers)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#2563eb', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL0.minPallets)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL0.actual)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL0.shortfall)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: theme.textPrimary }}>
                        {grandL0.minPallets > 0 ? Math.min(100, (grandL0.actual / grandL0.minPallets) * 100).toFixed(0) : 0}%
                      </td>
                      {canDeeper && <td style={td}></td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 38 }}>#</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Type</th>
                    <th style={{ ...th, textAlign: 'right' }}>Value</th>
                    <th style={{ ...th, textAlign: 'right' }}>×Factor</th>
                    <th style={{ ...th, textAlign: 'right' }}>Min Pallets</th>
                    <th style={{ ...th, textAlign: 'right' }}>Actual</th>
                    <th style={{ ...th, textAlign: 'right' }}>Shortfall</th>
                    <th style={{ ...th, textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRowsL1.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: 30, textAlign: 'center', color: theme.textMuted }}>No data</td></tr>
                  )}
                  {tableRowsL1.map((r, i) => (
                    <tr key={`${r.id || i}-${r.customer}`}
                      style={{ borderBottom: `1px solid ${theme.rowBorder}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f7faff')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                      <td style={{ ...td, color: theme.textMuted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 700, color: theme.textPrimary }}>
                        {aliasR('customer', r.customer) || '—'}
                        {aliases.customer?.[r.customer] && <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500, marginTop: 1 }}>{r.customer}</div>}
                        {r.notes && <div style={{ fontSize: 10, color: theme.textMuted, fontWeight: 500, marginTop: 2 }}>{r.notes}</div>}
                      </td>
                      <td style={{ ...td }}>
                        <span style={{
                          display: 'inline-block', padding: '3px 8px', borderRadius: 6,
                          fontSize: 10, fontWeight: 700,
                          background: '#eef2ff', color: '#3730a3',
                        }}>{r.type}</span>
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textPrimary }}>{fmtNum(r.value)} <span style={{ color: theme.textMuted, fontSize: 10 }}>{r.unit}</span></td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: theme.textMuted, fontSize: 11 }}>×{r.factor}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#2563eb', fontWeight: 700 }}>{fmtNum(r.minPallets)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#16a34a', fontWeight: 700 }}>{fmtNum(r.actual)}</td>
                      <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.shortfall > 0 ? '#dc2626' : '#16a34a', fontWeight: 800 }}>{r.shortfall > 0 ? `+${fmtNum(r.shortfall)}` : '0'}</td>
                      <td style={{ ...td, textAlign: 'center' }}><StatusPill met={r.isMet} /></td>
                    </tr>
                  ))}
                </tbody>
                {tableRowsL1.length > 0 && (
                  <tfoot>
                    <tr style={{ background: theme.panelBg }}>
                      <td style={{ ...td, fontWeight: 800, color: theme.textSecondary }} colSpan={5}>TOTAL</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#2563eb', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL1.minPallets)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#16a34a', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL1.actual)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 800, color: '#dc2626', fontVariantNumeric: 'tabular-nums' }}>{fmtNum(grandL1.shortfall)}</td>
                      <td style={td}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DrillModal;
